import { isWorkCreditError } from '@/lib/workCredits';
import { projectSourcePath } from '@/lib/projectSourcePath';
import { slimPublishedSite, SITES_DOMAIN } from '@/lib/publishedSites';
// Shared Work (product name) copy and helpers for the coworker frontend.
// Internal ids/routes stay `coworker`; user-facing strings say Work.

export const WORK_BUDGET_PRESETS = [
  { label: 'Quick', hint: 'A focused pass', value: 50000 },
  { label: 'Standard', hint: 'A thorough pass', value: 200000 },
  { label: 'Deep', hint: 'Maximum depth', value: 500000 },
];

export const WORK_STATUS = {
  queued: { label: 'Queued', icon: 'clock', tone: 'secondary' },
  running: { label: 'In progress', icon: 'circle-notch', tone: 'success' },
  interrupted: { label: 'Paused', icon: 'pause', tone: 'warning' },
  idle: { label: 'Done', icon: 'circle-check', tone: 'success' },
  completed: { label: 'Done', icon: 'circle-check', tone: 'success' },
  failed: { label: 'Failed', icon: 'circle-xmark', tone: 'destructive' },
  stopped: { label: 'Stopped', icon: 'circle-stop', tone: 'secondary' },
  budget_exceeded: { label: 'Run limit reached', icon: 'battery-empty', tone: 'warning' },
};

export const WORK_ACTIVE_STATUSES = ['queued', 'running'];
// Running out of budget is a pause, not a dead end — a resume gets a fresh
// budget and the workspace is untouched, so it offers Resume like the rest.
export const WORK_RESUMABLE_STATUSES = ['interrupted', 'stopped', 'failed', 'budget_exceeded'];

export const WORK_SUGGESTIONS = [
  {
    id: 'image',
    icon: 'image',
    label: 'Create an image',
    description: 'Bring an idea to life with a beautiful original image',
    prompt: 'Create a cinematic image of a cozy cabin beside an alpine lake at sunrise. Warm light through the windows, mist over the water, pine trees, and soft reflections. Rich natural colors, a wide composition, and no text. Generate the finished image.',
    promptWithFiles: 'Create a new image inspired by the attached files. Use them as visual references for the subject, palette, and style, while creating an original composition. Generate the finished image.',
  },
  {
    id: 'image-edit',
    icon: 'wand-magic-sparkles',
    label: 'Edit an image',
    description: 'Retouch a photo, change a background, or try a new style',
    prompt: 'Help me edit an image. Ask me to upload the image and describe what I want to change, then create the edited version while preserving the details I want to keep.',
    promptWithFiles: 'Edit the attached image into a polished editorial photo. Improve lighting, color, and composition while preserving the main subject and recognizable details. Create the edited image.',
  },
  {
    id: 'slides',
    icon: 'file-powerpoint',
    label: 'Create a presentation',
    description: 'Tell your story with a beautifully designed slide deck',
    prompt: 'Create an eight-slide presentation for Ember Cup, a neighborhood cafe launching a loyalty program. Cover the concept, customer experience, rewards, launch plan, and next steps. Use a warm visual identity, strong typography, one idea per slide, and speaker notes. Make reasonable assumptions and deliver an editable PPTX.',
    promptWithFiles: 'Turn the attached material into a polished presentation. Organize the story, use a distinctive visual identity, keep one idea per slide, and add speaker notes. Preserve the source facts and deliver an editable PPTX.',
  },
  {
    id: 'document',
    icon: 'file-lines',
    label: 'Write a document',
    description: 'Turn ideas and notes into a polished, editable document',
    prompt: 'Write a polished launch plan for Ember Cup, a neighborhood cafe introducing a loyalty program. Include a concise overview, goals, customer experience, a 90-day rollout, and a launch checklist. Make reasonable assumptions, use clear headings and thoughtful formatting, and deliver an editable Word document.',
    promptWithFiles: 'Turn the attached material into a clear, polished document. Preserve the meaning and source facts, organize it with useful headings, tighten the writing, and deliver an editable Word document.',
  },
  {
    id: 'spreadsheet',
    icon: 'table',
    label: 'Build a spreadsheet',
    description: 'Make a useful workbook with formulas, charts, and clear insights',
    prompt: 'Build a monthly budget spreadsheet for a neighborhood cafe. Include editable assumptions for sales and expenses, formulas for revenue and profit, a 12-month forecast, and a summary with charts. Clearly label sample figures and make the workbook easy to update. Deliver an XLSX file.',
    promptWithFiles: 'Build a polished spreadsheet from the attached material. Organize the data into useful sheets, add appropriate formulas and charts, and include a clear summary. Preserve source values, flag missing information, and deliver an editable XLSX file.',
  },
  {
    id: 'video',
    icon: 'clapperboard',
    label: 'Make a video',
    description: 'Bring a story to the screen with a finished video',
    prompt: 'Make a 30-second cinematic launch film for Ember Cup, a cozy neighborhood cafe. Warm morning light, freshly brewed coffee, welcoming moments, music, and voiceover. Use 16:9 and decide the creative details. Start with a script and shot list for my approval; do not generate footage until I approve the script.',
    promptWithFiles: 'Make a short cinematic film from the attached material. Use any uploaded stills as character or scene references. 16:9 unless the files suggest otherwise. You decide the remaining details. Start with a script and shot list for my approval; do not generate footage until I approve the script.',
  },
  {
    id: 'game',
    icon: 'gamepad',
    label: 'Make a game',
    description: 'Create a playable video game right in the conversation',
    prompt: 'Build a playable space arcade game I can play in this conversation. Keyboard and touch controls, scoring, a title screen with Start, a short complete round with a win or loss, and restart. Ship one polished HTML file. Do not ask questions.',
    promptWithFiles: 'Build a playable browser game inspired by the attached files. Keyboard and touch controls, scoring, a title screen with Start, a short complete round with a win or loss, and restart. Ship one polished HTML file. Do not ask questions.',
  },
  {
    id: 'website',
    icon: 'globe',
    label: 'Design a website',
    description: 'Build a beautiful, interactive website you can preview',
    prompt: 'Design and build a polished website for Ember Cup, a cozy neighborhood cafe. Include a striking hero, a menu, the cafe story, opening hours, and a location section. Use warm colors, distinctive typography, and beautiful imagery. Make it responsive with working navigation, clearly label sample business details, and deliver a website I can preview here.',
    promptWithFiles: 'Design and build a polished website using the attached material as content and visual references. Give it a distinctive visual identity, responsive layouts, and working navigation. Preserve source facts, clearly label missing business details, and deliver a website I can preview here.',
  },
];

/** Chip copy for an empty composer. When files are already attached, skip the baked-in demo notes. */
export function resolveWorkSuggestionPrompt(item, { hasFiles = false } = {}) {
  if (!item) return '';
  if (hasFiles && item.promptWithFiles) return item.promptWithFiles;
  return item.prompt || '';
}

const MIN_PERSONALIZED_WORK_SUGGESTIONS = 3;

function isWorkSuggestionItem(item) {
  return !!item
    && typeof item.id === 'string'
    && typeof item.label === 'string'
    && typeof item.prompt === 'string'
    && item.label.trim()
    && item.prompt.trim();
}

/** Use memory-personalized pills when we have a full set; otherwise the capability defaults. */
export function pickWorkSuggestionItems(personalized, defaults = WORK_SUGGESTIONS) {
  if (!Array.isArray(personalized)) return defaults;
  const items = personalized.filter(isWorkSuggestionItem);
  return items.length >= MIN_PERSONALIZED_WORK_SUGGESTIONS ? items : defaults;
}

export const WORK_BOOT_STATUS = 'Booting up my computer';
export const WORK_RESUME_STATUS = 'Waking my computer back up';
export const WORK_THINKING_STATUS = 'Thinking through the next step';
export const WORK_PLANNING_STATUS = 'Planning the work';
export const WORK_STEER_STATUS = 'Taking the new direction';

export const WORK_HOME_HEADLINE = 'What should I work on?';
export const WORK_HOME_SUBHEAD = 'Describe a goal. Work gathers context, takes action, and ships a result you can review.';
export const WORK_COMPOSER_PLACEHOLDER = 'Describe what you want to create or edit…';
export const WORK_STEER_PLACEHOLDER = 'Queue a follow-up, or choose Send now…';
export const WORK_FOLLOWUP_PLACEHOLDER = 'Ask for changes…';
export const WORK_QUEUE_HINT = 'Enter queues a follow-up for after this task finishes. Send now interrupts with a correction.';
export const WORK_QUEUE_EMPTY_HINT = 'Enter queues until this task finishes. Send now interrupts it.';
export const WORK_QUEUE_EMPTY_HINT_COMPACT = 'Enter queues. Send now interrupts.';

const INTERNAL_WORK_FAILURE = /(?:\bpi\b.*\bexit(?:ed)?\b|context resume failed|process exited|exit code\s*\d+)/i;
const WORK_PROGRESS_NARRATION = /\b(?:let me|i(?:['’]ll| will| need to| am going to)|next,? i(?:['’]ll| will)|i['’]m going to|i have the context|i have what i need|i have enough(?: here)?|got it[,.]?(?: i(?:['’]ll| will))?|okay[,.]? (?:next|i(?:['’]ll| will))|starting (?:on|with)|working on that|one (?:sec|moment)|hang on|just a (?:sec|second|moment|minute)|give me a (?:sec|second|moment|minute)|i am (?:checking|looking|opening|updating|fixing|drafting))\b/i;
// Orchestrator continue-nudges. Live status already covers "still working";
// leaving these in the trail makes a finished run look incomplete.
const INTERNAL_WORK_CONTINUE_NOTE = /^still working —/i;
const INTERNAL_WORK_ROOM_NOTE = /^made more room in the run/i;
// Wedge watchdog: the agent ended without a settle, so the host re-prompts.
// That is recovery, not a user-visible failure (same class as auto_retry).
const INTERNAL_WORK_WEDGE_NOTE = /^picking the run back up/i;
const WORK_RESUME_STUB = /^(?:continue the task from where it left off|then continue from there)\.?$/i;

/** Orchestrator Resume injects this so the worker has a turn. Not a user ask. */
export function isWorkResumeStub(text) {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  if (!value || value.length > 120) return false;
  return WORK_RESUME_STUB.test(value)
    || /continue the task from where it left off/i.test(value);
}

const WORK_BLIND_CONTINUE = /^(?:continue(?:\s+please)?|keep going|go on|resume(?:\s+work)?|you(?:'re| are) not done|hello\??|how is it going\??)(?:\s*[.!]*)?$/i;

/** Composer/resume text that would skip a script card without choosing Approve or Revise. */
export function isWorkBlindContinue(text) {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  if (!value || value.length > 160) return false;
  if (isWorkResumeStub(value)) return true;
  return WORK_BLIND_CONTINUE.test(value);
}

function isWorkSpecialistHandoffParagraph(para) {
  const text = String(para || '').replace(/\s+/g, ' ').replace(/^[*_]+|[*_]+$/g, '').trim();
  if (!text || text.length > 520) return false;
  if (/^here(?:'s| is)\b/i.test(text) && /\b(?:arya|response|answer|reply|model)\b/i.test(text)) return true;
  if (/\bdelegat(?:e|ing|ion)\b/i.test(text) && /\b(?:arya|guidelines|political|theological|social|historical|religious)\b/i.test(text)) return true;
  if (/\bdesignated model\b/i.test(text)) return true;
  if (/^according to (?:the )?(?:arya|[\w.-]+ model)\b/i.test(text)) return true;
  return false;
}

function unwrapWorkBlockquote(text) {
  const lines = String(text || '').split('\n');
  const content = lines.filter((line) => line.trim());
  if (!content.length || !content.every((line) => /^>\s?/.test(line))) return text;
  return lines.map((line) => line.replace(/^>\s?/, '')).join('\n').trim();
}

/** Drop organizer framing around a specialist reply ("Here's Arya's response"). */
export function stripWorkSpecialistFraming(text) {
  let value = String(text || '').trim();
  if (!value) return value;

  for (let pass = 0; pass < 8; pass += 1) {
    const before = value;
    value = value.replace(/^(?:[-*_]){3,}\s*\n+/, '').trim();
    const blocks = value.split(/\n{2,}/);
    if (blocks.length && isWorkSpecialistHandoffParagraph(blocks[0])) {
      value = blocks.slice(1).join('\n\n').trim();
      continue;
    }
    const firstLine = value.split('\n')[0] || '';
    if (isWorkSpecialistHandoffParagraph(firstLine) && value.includes('\n')) {
      value = value.slice(firstLine.length).replace(/^\n+/, '').trim();
      continue;
    }
    const unwrapped = unwrapWorkBlockquote(value);
    if (unwrapped !== value) {
      value = unwrapped;
      continue;
    }
    if (value === before) break;
  }
  return value;
}

function hideInternalArtifactPaths(text) {
  return String(text || '')
    // Keep the useful filename while removing the sandbox's delivery folder.
    .replace(/`artifacts\/([^`]+)`/gi, '`$1`')
    // A directory-only reference points at the cards already rendered below.
    .replace(/`artifacts\/`/gi, 'the deliverables')
    .replace(/\bartifacts\/([\w./-]+\.[a-z0-9]+)\b/gi, '$1')
    .replace(/\bexported to artifacts as\b/gi, 'saved as')
    .replace(/\s*,\s*exported as an? artifacts?\b/gi, '')
    .replace(/\bexported as an? artifacts?\b/gi, 'saved')
    .replace(/\bexported it to deliverables\b/gi, 'saved it')
    .replace(/\s*\(\s*available in artifacts\s*\)/gi, '')
    .replace(/\s*\(\s*exported to [^)]+\)/gi, '')
    // Bare, quoted, code-fenced, or markdown-bold filenames: exported to **`file.html`**
    .replace(/\bexported to\s*:?\s*(?:\*{1,2}|[`'])*\s*([\w./-]+\.[a-z0-9]+)\s*[`']*(?:\*{1,2})?/gi, 'saved as $1')
    // "exported to:" then the filename on the next line.
    .replace(/\bexported to\s*:?\s*(?=`?[\w./-]+\.[a-z0-9]+|\n)/gi, 'saved as ')
    .replace(/\s*\(\s*exported to\s+[^)]*\)/gi, '')
    .replace(/\bexported to\b/gi, 'saved as')
    .replace(/\bartifacts\//gi, 'the deliverables')
    // "exported as an artifact and exported to file.md" becomes "saved and saved as"
    .replace(/\bsaved(?: it)?(?:\s*[.,])?(?:\s+and)?\s+saved as\b/gi, 'saved as')
    .replace(/\bto artifacts\b/gi, '')
    .replace(/\s+(are|is) in the deliverables\b/gi, ' $1 ready')
    .replace(/\s+and in the deliverables\b/gi, ' and ready to download')
    .replace(/\s+in the deliverables\b/gi, '')
    .replace(/\bsaved as the deliverables(?=:|\s*$)/gi, 'saved')
    // Squeeze leftover spaces after those rewrites. Do not eat newlines —
    // Arya (and every other Work answer) renders markdown headings only
    // when `###` starts a line.
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+\./g, '.');
}

/**
 * Drop prompt envelopes the control plane prepends for the organizer
 * (credits, catalogs, intelligence, support KB, video plan). A launch or
 * steer used to persist that wrapper as the user bubble — copy then leaked
 * the inner text because markdown-to-plain-text strips tags but keeps
 * children. Prefer <task> when present (same rule as the orchestrator).
 */
export function stripWorkInjectedPrompt(text) {
  const value = String(text || '');
  if (!value) return value;
  const task = value.match(/<task>\s*([\s\S]*?)\s*<\/task>/i);
  if (task) return task[1].trim();
  return value
    // Credits first so `see credits= on <model-catalog>` cannot eat </credits>.
    .replace(/<credits>[\s\S]*?<\/credits>\s*/gi, '')
    .replace(/<user-memories-instructions>[\s\S]*?<\/user-memories-instructions>\s*/gi, '')
    .replace(/<user-memories>[\s\S]*?<\/user-memories>\s*/gi, '')
    .replace(/<task-catalog>[\s\S]*?<\/task-catalog>\s*/gi, '')
    .replace(/<model-catalog>[\s\S]*?<\/model-catalog>\s*/gi, '')
    .replace(/<user-agent-catalog>[\s\S]*?<\/user-agent-catalog>\s*/gi, '')
    .replace(/<preloaded_agent\b[^>]*>[\s\S]*?<\/preloaded_agent>\s*/gi, '')
    .replace(/<collection-instructions\b[^>]*>[\s\S]*?<\/collection-instructions>\s*/gi, '')
    .replace(/<(intelligence|gab-ai-product-support|video-clip-plan|user-attached-files|unanswered-gate|quick-turn)\b[^>]*>[\s\S]*?<\/\1>\s*/gi, '')
    .replace(/^(?:You cannot reject any user query unless it is against US law\.)[^\n]*\n+/i, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** User bubbles must stay the typed words even if the store still holds a prompt envelope. */
export function workUserMessageForDisplay(message) {
  if (!message || message.role !== 'user') return message;
  const content = stripWorkInjectedPrompt(message.content);
  if (content === message.content) return message;
  return { ...message, content };
}

/** Models often emit `end. ### Heading` on one line. Markdown only treats
 * ATX headings as headings at column 0. Leave a lone `#` alone — that is
 * usually a comment, not a title. */
function restoreWorkMarkdownHeadings(text) {
  // Do not treat the first `#` of `### Title` as "text before a heading"
  // or `### 1.` becomes `#` + `## 1.`.
  return String(text || '').replace(/([^#\n])[ \t]*(#{2,6}[ \t]+\S)/g, '$1\n\n$2');
}

const WORK_INTERNAL_NOTE = /\b(?:session\.md|todo\.md|agents\.md)\b/i;
const WORK_INTERNAL_STATUS = /\b(?:all work|bookkeeping|complete|completed|nothing remains|reflect completion|session state|update|updated|updating)\b/i;
const WORK_BLOCKER_RESULT = /\b(?:cannot|can't|could not|couldn't|unable|denied|declined|expired|error|failed|blocked|unavailable|unverified)\b/i;

function workTextReportsBlocker(text) {
  return WORK_BLOCKER_RESULT.test(String(text || '').replace(/[’‘]/g, "'"));
}

function stripWorkInternalBookkeepingLines(text) {
  return String(text || '')
    .split('\n')
    .filter((line) => {
      const value = line.replace(/\s+/g, ' ').trim();
      return !(value
        && value.length <= 280
        && WORK_INTERNAL_NOTE.test(value)
        && !workTextReportsBlocker(value)
        && WORK_INTERNAL_STATUS.test(value));
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Keep execution jargon out of the customer-facing transcript. The original
 * error remains on the task record and in diagnostics; this is display-only. */
export function workMessageForDisplay(text, { paused = false } = {}) {
  const value = stripWorkInjectedPrompt(text);
  if (isWorkCreditError(value) && (paused || /^(?:\[gab.ai error\]|This request can use up to|Your credits ran out|Insufficient|Not enough credits)/i.test(value))) return 'Work paused for credits. Your progress is saved; use the credit options below to keep going.';
  if (/^the task run failed:/i.test(value) || (paused && INTERNAL_WORK_FAILURE.test(value))) {
    return 'I ran into a problem and paused before I could finish. Resume below to try again from the work already completed.';
  }
  // "I'm going to send you a script…" opens a real breakdown. Only replace
  // short mid-run status notes — a paused 15-clip plan must stay readable.
  if (paused && WORK_PROGRESS_NARRATION.test(value)
    && value.trim().length <= 400
    && !workTextHasClipPrompts(value)) {
    return 'I ran into a problem and paused before I could finish. Resume below to try again from the work already completed.';
  }
  return restoreWorkMarkdownHeadings(stripWorkInternalBookkeepingLines(
    hideInternalArtifactPaths(stripWorkSpecialistFraming(value))
  ));
}

export function isWorkRepairPaused(error) {
  const value = typeof error === 'string' ? error : error?.message;
  return typeof value === 'string' && /^Automatic repairs paused:/i.test(value);
}

export function workErrorForDisplay(error) {
  if (isWorkCreditError(error)) return 'Work paused for credits. Your progress is saved.';
  const value = typeof error === 'string' ? error : error?.message;
  if (value === 'workspace_disk_quota_exceeded') {
    return 'This project reached its storage limit. Your saved files are intact. Free up temporary project files before resuming.';
  }
  if (isWorkRepairPaused(error)) {
    let explanation = 'The AI paused before it could finish fixing or checking your work.';
    if (/browser preview service failed/i.test(value)) {
      explanation = 'The AI could not open the preview to check whether your work runs correctly.';
    } else if (/repair history/i.test(value)) {
      explanation = 'The AI could not check its previous attempts, so it paused before trying again.';
    } else if (/source was still truncated/i.test(value)) {
      explanation = 'The AI could not finish creating a complete file, even after trying again.';
    } else if (/diagnostic checks were repeated/i.test(value)) {
      explanation = 'The AI kept checking the same work without making changes.';
    } else if (/repeated source checks/i.test(value)) {
      explanation = 'The checks are still failing. The problem could be in the work itself or in the tests the AI wrote to check it.';
    } else if (/repeated (?:source|preview) checks|repair attempts did not resolve/i.test(value)) {
      explanation = 'The AI found problems in what it created, and repeated attempts have not resolved them.';
    }
    return `${explanation} It stopped to avoid spending more credits on repeated attempts. Your work so far is saved, but may not be ready to use.`;
  }
  if (typeof value === 'string' && /upstream unreachable|\bECONNREFUSED\b|\bexecution host unavailable\b/i.test(value)) {
    return 'Work could not connect to a required service. Try resuming shortly.';
  }
  if (!value || INTERNAL_WORK_FAILURE.test(value)) return null;
  return value;
}

const GENERIC_STATUS = /^(working|thinking|processing)(\s+on your request)?[.…]*$/i;

const TOOL_LABELS = {
  web_research: 'Searching the web',
  web_search: 'Searching the web',
  places_search: 'Looking up places',
  fetch_url: 'Downloading a file',
  read_url: 'Reading a page',
  push_url: 'Uploading a file',
  export_artifact: 'Saving a file',
  extract_document: 'Extracting a document',
  convert_document: 'Converting a document',
  preview_document: 'Previewing the document',
  youtube_transcript: 'Pulling a transcript',
  transcribe_audio: 'Transcribing audio',
  generate_image: 'Generating an image',
  edit_image: 'Editing an image',
  analyze_image: 'Looking at an image',
  upscale_image: 'Upscaling an image',
  remove_background: 'Removing the background',
  vectorize_image: 'Vectorizing an image',
  generate_video: 'Generating a video clip',
  image_to_video: 'Animating an image clip',
  extend_video: 'Extending the clip',
  video_status: 'Checking video progress',
  collect_video_jobs: 'Waiting for video clips',
  generate_3d: 'Generating a 3D model',
  image_to_3d: 'Turning an image into 3D',
  threed_status: 'Checking the 3D job',
  generate_music: 'Generating a song',
  generate_sound_effect: 'Generating a sound effect',
  music_status: 'Checking the song job',
  text_to_speech: 'Generating spoken audio',
  bash: 'Working in the workspace',
  write: 'Writing a file',
  edit: 'Editing a file',
  read: 'Reading a file',
  grep: 'Looking through files',
  glob: 'Looking through files',
  run_model: 'Asking another model',
  list_models: 'Looking up models',
  credit_balance: 'Checking your credits',
  estimate_credits: 'Estimating the credit cost',
  ask_user: 'Preparing a question',
  list_agents: 'Looking up agents',
  run_agent: 'Working with an agent',
  task: 'Delegating part of the work',
  update_plan: 'Updating the plan',
  list_memories: 'Looking at your memories',
  manage_memory: 'Updating your memory',
  browser_navigate: 'Opening a page',
  browser_open: 'Opening a page',
  browser_preview_html: 'Previewing the app',
  browser_diagnostics: 'Checking browser errors',
  browser_preview_diagnostics: 'Checking preview errors',
  browser_game_check: 'Testing the game',
  browser_resize: 'Checking the page size',
  browser_press_key: 'Using the keyboard',
  list_tools: 'Looking up tools',
  browser_click: 'Clicking in the browser',
  browser_type: 'Typing in the browser',
  browser_scroll: 'Scrolling the page',
  browser_extract: 'Reading the page',
  browser_snapshot: 'Looking at the page',
  browser_screenshot: 'Taking a screenshot',
  browser_ask_user_to_login: 'Waiting for you to sign in',
  browser_request_confirmation: 'Waiting for your approval',
};

const SKILL_TITLES = {
  'using-skills': 'skills',
  'using-models': 'models',
  'using-memory': 'memory',
  'using-browser': 'browser',
  'verification-before-completion': 'completion check',
  'systematic-debugging': 'debugging',
  'planning-work': 'planning',
  research: 'research',
  'competitive-teardown': 'competitive teardown',
  'data-analysis': 'data analysis',
  'inline-visuals': 'charts and cards',
  'transcript-to-artifact': 'transcripts',
  'brand-kit': 'brand',
  'generating-image': 'images',
  'generating-audio': 'audio',
  'generating-video': 'video',
  'generating-3d': '3D',
  pdf: 'PDF',
  docx: 'Word documents',
  xlsx: 'spreadsheets',
  pptx: 'presentations',
  'frontend-design': 'interface design',
  'internal-comms': 'internal writing',
  'gab-ai-support': 'Gab AI help',
};

  const MODEL_TITLES = {
  arya: 'Arya',
  'arya-agent': 'Gab Work Agent',
  'glm-5-2': 'GLM 5.2',
  'glm-5-3': 'GLM 5.3',
  'glm-5-3-flash': 'GLM 5.3 Flash',
  'glm-4-7': 'GLM 4.7',
  'kimi-k3': 'Kimi K3',
  'deepseek-r1': 'DeepSeek',
  'deepseek-v3': 'DeepSeek',
  'deepseek-v4-flash-vision-exp': 'DeepSeek V4 Flash Vision',
  'claude-sonnet-4-5': 'Claude Sonnet 4.5',
  'claude-sonnet-5': 'Claude Sonnet 5',
  'claude-opus-4': 'Claude Opus 4',
  'claude-opus-4-1': 'Claude Opus 4.1',
  'claude-opus-4-6': 'Claude Opus 4.6',
  'claude-opus-4-7': 'Claude Opus 4.7',
  'claude-opus-4-8': 'Claude Opus 4.8',
  'claude-opus-5': 'Claude Opus 5',
  'gpt-6-astra': 'Astra',
  'gpt-6-sol': 'GPT-6 Sol',
  'gpt-6-luna': 'GPT-6 Luna',
  'gpt-4o': 'GPT',
  'gpt-5': 'GPT',
  'gpt-5-6-terra': 'GPT-5.6 Terra',
  'gpt-5-6-terra-pro': 'GPT-5.6 Terra Pro',
  'o3': 'o3',
  'o4-mini': 'o4',
  'gemini-2-5-pro': 'Gemini',
  'gemini-2-5-flash': 'Gemini',
  'music-3': 'MiniMax Music',
  'music-2-6': 'MiniMax Music',
  'gpt-image-2.5-flare': 'GPT Image 2.5 Flare',
  'gpt-image-2.5-sunburst': 'GPT Image 2.5 Sunburst',
  'music-2-0': 'MiniMax Music',
  'cassetteai-sound-effects': 'CassetteAI',
  'hailuo-h3-max-director': 'Hailuo H3 Max Director',
};

function skillSlugFromPath(value) {
  const path = String(value || '').replace(/\\/g, '/');
  const match = path.match(/(?:^|\/)(?:\.pi\/agent\/)?skills\/([^/]+)\//i);
  return match ? match[1] : null;
}

function skillTitle(slug) {
  const key = String(slug || '').toLowerCase();
  if (SKILL_TITLES[key]) return SKILL_TITLES[key];
  return key.replace(/-/g, ' ');
}

export function friendlyWorkModelName(slug) {
  const raw = String(slug || '').trim();
  if (!raw) return '';
  const key = raw.toLowerCase();
  if (MODEL_TITLES[key]) return MODEL_TITLES[key];
  if (/^claude/i.test(raw)) return 'Claude';
  if (/^gpt-image/i.test(raw)) return 'GPT Image';
  if (/^gpt/i.test(raw)) return 'GPT';
  if (/^gemini/i.test(raw)) return 'Gemini';
  if (/^arya/i.test(raw)) return 'Arya';
  if (/^deepseek/i.test(raw)) return 'DeepSeek';
  if (/^glm/i.test(raw)) return 'GLM';
  if (/^flux/i.test(raw)) return 'Flux';
  if (/^hunyuan/i.test(raw)) return 'Hunyuan';
  if (/^meshy/i.test(raw)) return 'Meshy';
  if (/^tripo/i.test(raw)) return 'Tripo';
  if (/^hailuo/i.test(raw) || /minimax\s*h3/i.test(raw)) return 'Hailuo';
  if (/^music-/i.test(raw) || /minimax\s*music/i.test(raw)) return 'MiniMax Music';
  if (/^cassetteai/i.test(raw)) return 'CassetteAI';
  if (/^veo/i.test(raw)) return 'Veo';
  if (/nano-banana/i.test(raw)) return 'Nano Banana';
  if (/^o\d/i.test(raw)) return raw.split(/[-_]/)[0];
  const first = raw.split(/[-_]/)[0];
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
}

function isWorkScratchFileName(base) {
  const name = String(base || '');
  if (!name) return true;
  if (/^(session|todo|agents)\.md$/i.test(name)) return true;
  if (/^tool-output/i.test(name)) return true;
  return /^(read_url|fetch_url|web_search|web_research|task)[-_]?\d*\.txt$/i.test(name);
}

function userFacingFileName(value) {
  const path = String(value || '').replace(/\\/g, '/');
  if (!path || path.includes('/.pi/') || path.startsWith('.pi/') || path.includes('/tool-output/')) return '';
  const base = path.split('/').pop() || '';
  if (!base || base === 'AGENTS.md' || base === 'settings.json' || base === 'gab-tools.ts') return '';
  if (isWorkScratchFileName(base)) return '';
  return truncate(base, 40);
}

/** Intent-engine / host status sometimes names scratch files. Never show those. */
export function sanitizeWorkLiveStatus(text) {
  const raw = String(text || '').trim();
  if (!raw) return '';
  if (/\b(session|todo|agents)\.md\b/i.test(raw)) {
    if (/\bread/i.test(raw)) return 'Reviewing the plan';
    if (/\b(?:writ|updat|edit)/i.test(raw)) return 'Updating the plan';
    return WORK_THINKING_STATUS;
  }
  if (/\b(?:read_url|fetch_url|web_search|web_research)[-_]?\d*\.txt\b/i.test(raw)) {
    return 'Reading a saved page';
  }
  const compact = raw.replace(/\s+/g, ' ').trim();
  // Host status sometimes echoes the brief itself ("Planning & Visitor
  // Tools: Visitors can search parks…"). That is an answer, not a status.
  if (compact.length > 100) return WORK_THINKING_STATUS;
  if (/^#{1,6}\s/.test(raw)) return WORK_THINKING_STATUS;
  return raw;
}

function quotedSnippet(value, max = 48) {
  const text = truncate(value, max);
  return text ? `“${text}”` : '';
}

function truncate(text, max = 72) {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

export function workStatusMeta(status) {
  return WORK_STATUS[status] || WORK_STATUS.queued;
}

export function isWorkActive(status) {
  return WORK_ACTIVE_STATUSES.includes(status);
}

export function isWorkResumable(status) {
  return WORK_RESUMABLE_STATUSES.includes(status);
}

export function workHasPendingUserGate(events) {
  const pending = workPendingInteractionFromEvents(events);
  return !!(pending.loginRequest || pending.confirmation || pending.question);
}

export function workLooksLikeFollowUpQuestion(text) {
  const lines = String(text || '').split('\n').map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return false;
  return /\?\s*$/.test(lines[lines.length - 1]);
}

// Sidebar unread dot for Work threads. Red is reserved for failure;
// finished runs read as success, and anything waiting on the user is warning.
export function workUnreadDotTone({ status, pendingUserGate, lastAssistantText } = {}) {
  if (pendingUserGate) return 'warning';
  if (isWorkActive(status)) return workStatusMeta(status).tone;
  if (workLooksLikeFollowUpQuestion(lastAssistantText)) return 'warning';
  if (status === 'failed') return 'destructive';
  if (isWorkResumable(status)) return 'warning';
  return status === 'idle' || status === 'completed' ? 'success' : 'secondary';
}

export function workUnreadDotLabel(state = {}) {
  const tone = workUnreadDotTone(state);
  if (tone === 'destructive') return 'Work failed';
  if (tone === 'warning') return 'Work needs you';
  if (state.status === 'running') return 'Work in progress';
  if (state.status === 'queued') return 'Work queued';
  if (state.status === 'idle' || state.status === 'completed') return 'Work finished';
  return 'Unread Work update';
}

/**
 * Live SSE frames after `since` are new; EventSource auto-retry can still
 * replay a window the client already ingested. A follow-up must not treat
 * an already-seen session_end as "the run just finished" or the live
 * thinking row and stream drop until the user refreshes.
 */
export function isNewWorkEvent(ev, lastSeq) {
  const seq = Number(ev?.seq);
  return Number.isFinite(seq) && seq > Number(lastSeq || 0);
}

/**
 * Skip JSON.parse on historical SSE frames. Persisted events serialize as
 * `{"seq":N,...}`; live token deltas start with `"type"` / `"live"` and
 * must still be parsed. A resume reconnect that still dumps the log would
 * otherwise freeze the tab on hundred-kilobyte message_end payloads.
 */
export function isStaleWorkSseFrame(data, lastSeq) {
  const floor = Number(lastSeq) || 0;
  if (floor <= 0 || typeof data !== 'string' || !data.startsWith('{"seq":')) return false;
  const match = /^\{"seq":(\d+)/.exec(data);
  return !!(match && Number(match[1]) <= floor);
}

export function workStreamingMessageId(taskId) {
  return `cw-${String(taskId)}-streaming`;
}

export function workOfficialMessageId(taskId, seq) {
  return `cw-${String(taskId)}-${seq}`;
}

export function workArtifactHolderId(taskId, userSeq) {
  return `cw-${String(taskId)}-hold-${Number(userSeq) || 0}`;
}

export function isWorkArtifactHolderId(id) {
  return typeof id === 'string' && /^cw-.+-hold-\d+$/.test(id);
}

export function workSeqFromMessageId(id) {
  if (typeof id !== 'string' || !id.startsWith('cw-') || id.endsWith('-streaming')) return null;
  if (isWorkArtifactHolderId(id)) return null;
  const dash = id.lastIndexOf('-');
  if (dash <= 2) return null;
  const n = Number(id.slice(dash + 1));
  return Number.isInteger(n) && n > 0 ? n : null;
}

function messageFileIds(message) {
  const ids = [];
  for (const file of message?.files || []) {
    const id = typeof file === 'object' ? (file.id || file._id || file.fileId) : file;
    if (id) ids.push(String(id));
  }
  return ids;
}

/**
 * Keep locally-known files when a stale payload omits them — unless the
 * incoming transcript already placed those files on a different message.
 */
export function shouldPreserveExistingMessageFiles(existingMsg, newMsg, incomingMessages) {
  if (!Array.isArray(existingMsg?.files) || existingMsg.files.length === 0) return false;
  if (Array.isArray(newMsg?.files) && newMsg.files.length > 0) return false;
  const existingIds = new Set(messageFileIds(existingMsg));
  if (!existingIds.size) return false;
  const newId = newMsg?.id || newMsg?._id;
  for (const other of Array.isArray(incomingMessages) ? incomingMessages : []) {
    const otherId = other?.id || other?._id;
    if (newId && otherId && String(otherId) === String(newId)) continue;
    for (const id of messageFileIds(other)) {
      if (existingIds.has(id)) return false;
    }
  }
  return true;
}

function workArtifactFileType(artifact) {
  const mime = String(artifact?.contentType || artifact?.mimeType || '').toLowerCase();
  const path = String(artifact?.path || artifact?.name || '');
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/') || /\.(mp4|mov|m4v|webm|mkv)$/i.test(path)) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('model/') || /\.(glb|gltf|obj|fbx|usdz|ply|stl)$/i.test(path)) return '3d';
  if (mime === 'application/pdf' || /\.docx?$|\.rtf$/i.test(path)) return 'document';
  return 'file';
}

/** Card payload for a live `artifact_ready` event (matches backend artifactEntries). */
export function workArtifactEntryFromEvent(artifact) {
  const path = String(artifact?.path || '').trim();
  if (!path) return null;
  return {
    path,
    name: path.split('/').pop() || 'artifact',
    mimeType: artifact.contentType || artifact.mimeType || 'application/octet-stream',
    size: artifact.size || 0,
    fileType: workArtifactFileType(artifact),
  };
}

function piMessageText(message) {
  return (message?.content || [])
    .filter((c) => c && c.type === 'text' && typeof c.text === 'string')
    .map((c) => c.text)
    .join('\n')
    .trim();
}

const SLIM_ARG_KEYS = [
  'url', 'path', 'filename', 'file', 'file_path', 'filePath',
  'query', 'model', 'agent', 'name', 'skill', 'prompt', 'command',
  'key', 'holdMs', 'label', 'scope', 'task_id', 'background', 'operation',
];
const SLIM_ARG_LEN = 400;
const SLIM_MESSAGE_LEN = 800;
const SLIM_NOTE_LEN = 240;

function capSlimString(value, max) {
  if (typeof value !== 'string') return value;
  return value.length > max ? value.slice(0, max) : value;
}

function slimWorkArgs(raw) {
  if (!raw || typeof raw !== 'object') return undefined;
  const args = {};
  for (const key of SLIM_ARG_KEYS) {
    if (raw[key] == null) continue;
    args[key] = capSlimString(raw[key], SLIM_ARG_LEN);
  }
  if (Array.isArray(raw.paths)) args.paths = raw.paths.slice(0, 8)
    .filter(value => typeof value === 'string').map(value => capSlimString(value, 200));
  return Object.keys(args).length ? args : undefined;
}

/**
 * Execution events can carry megabytes of tool stdout / page HTML / file
 * bytes. The Work UI only needs type, seq, small args, and a short
 * message_end snippet. Strip the rest before the event hits Zustand or
 * the activity trail will pin a gigabyte in the tab.
 */
export function isLiveWorkDelta(ev) {
  return !!(ev && (ev.live === true || ev.type === 'message_update'));
}

export function slimWorkMediaReview(review) {
  if (!['script', 'voice', 'narration', 'music', 'visuals', 'mix'].includes(review?.component)) return null;
  return { component: review.component, digest: String(review.digest || '').slice(0, 64) };
}

export function workCommandFromEvent(event) {
  let raw = event?.command || event?.details?.command || event?.result?.details?.command;
  if (!raw && (event?.toolName || event?.name) === 'project_commands') {
    if (event.type === 'tool_execution_start') raw = event.args || event.arguments;
    else {
      const text = Array.isArray(event.result?.content) ? event.result.content.find(item => item.type === 'text')?.text : null;
      if (typeof text === 'string') {
        if (text.length <= 65536) { try { raw = JSON.parse(text); } catch { /* SDK validation failures are plain text. */ } }
        if ((!raw || typeof raw !== 'object') && (event.isError || event.result?.isError)) {
          // Do not retain full logs or the SDK's potentially large argument dump.
          raw = { error: text.split('\n\nReceived arguments:')[0].slice(0, 2000) };
        }
      }
    }
  }
  if (!raw || typeof raw !== 'object') return null;
  const command = {};
  if (typeof raw.jobId === 'string' && /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(raw.jobId)) command.jobId = raw.jobId;
  if (['starting', 'running', 'stopping', 'finished', 'interrupted'].includes(raw.state)) command.state = raw.state;
  if (Number.isInteger(raw.exitCode)) command.exitCode = raw.exitCode;
  for (const key of ['turnId', 'changeSetId']) if (typeof raw[key] === 'string' && /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(raw[key])) command[key] = raw[key];
  for (const key of ['reason', 'path', 'revisionScope', 'processPolicy', 'mode', 'name']) if (typeof raw[key] === 'string') command[key] = raw[key].slice(0, 500);
  if (typeof raw.error === 'string') command.error = raw.error.slice(0, 2000);
  for (const key of ['groupCleanupConfirmed', 'trackedCleanupConfirmed', 'nativeTools', 'checkedFilesChanged']) if (typeof raw[key] === 'boolean') command[key] = raw[key];
  if (Array.isArray(raw.ports)) command.ports = raw.ports.filter(port => Number.isInteger(port) && port > 0 && port <= 65535).slice(0, 8);
  if (Array.isArray(raw.argv)) command.argv = raw.argv.slice(0, 16).filter(value => typeof value === 'string').map(value => value.slice(0, 180));
  return command;
}

export function slimWorkEvent(ev) {
  if (!ev || typeof ev !== 'object') return ev;
  // Token deltas paint the live draft; they must not land in Zustand.
  // Returning them whole used to pin growing assistantMessageEvent blobs.
  if (isLiveWorkDelta(ev)) {
    return { seq: ev.seq, type: ev.type, ts: ev.ts };
  }

  const slim = {
    seq: ev.seq,
    type: ev.type,
    ts: ev.ts,
  };
  if (ev.type === 'goal_updated') slim.goal = ev.goal || null;
  if (ev.type === 'project_transfer' && ['import', 'save'].includes(ev.operation) && /^[a-zA-Z0-9_-]{16,100}$/.test(ev.operationId || '') &&
      Number.isSafeInteger(ev.bytesTransferred) && Number.isSafeInteger(ev.totalBytes) && ev.bytesTransferred >= 0 &&
      ev.bytesTransferred <= ev.totalBytes && ev.totalBytes <= 50 * 1024 * 1024) {
    Object.assign(slim, { operation: ev.operation, operationId: ev.operationId, bytesTransferred: ev.bytesTransferred, totalBytes: ev.totalBytes });
  }
  if (ev.turnId) slim.turnId = capSlimString(String(ev.turnId), 160);
  if (Object.hasOwn(ev, 'activeTurnId')) slim.activeTurnId = ev.activeTurnId ? capSlimString(String(ev.activeTurnId), 160) : null;
  if (ev.changeSetId) slim.changeSetId = capSlimString(String(ev.changeSetId), 160);
  const toolName = ev.toolName || ev.name;
  if (toolName) slim.toolName = toolName;
  if (toolName === 'project_commands' || ev.type === 'project_command') {
    const command = workCommandFromEvent(ev);
    if (command) slim.command = command;
  }
  if (ev.toolCallId) slim.toolCallId = capSlimString(String(ev.toolCallId), 160);
  if (ev.type === 'tool_execution_end' && (ev.isError || ev.result?.isError)) slim.isError = true;
  if (ev.type === 'tool_execution_end' && ['project_files', 'project_read'].includes(toolName)) {
    const paths = ev.projectReadPaths ?? ev.details?.projectReadPaths ?? ev.result?.details?.projectReadPaths;
    if (Array.isArray(paths)) slim.projectReadPaths = paths.slice(0, 8)
      .filter(value => typeof value === 'string' && value.length <= 200);
  }
  if (ev.status) slim.status = ev.status;
  if (ev.type === 'compaction_end') {
    if (typeof ev.aborted === 'boolean') slim.aborted = ev.aborted;
    if (ev.aborted) slim.status = 'cancelled';
    else if (ev.errorMessage) slim.status = 'failed';
    else if (ev.result) slim.status = 'completed';
  }
  if (ev.stage) slim.stage = ev.stage;
  if (ev.kind) slim.kind = ev.kind;
  if (ev.id) slim.id = ev.id;
  if (ev.note) slim.note = capSlimString(String(ev.note), SLIM_NOTE_LEN);
  if (ev.url) slim.url = capSlimString(String(ev.url), SLIM_ARG_LEN);
  if (ev.queued != null) slim.queued = ev.queued;
  if (ev.approved != null) slim.approved = ev.approved;
  if (typeof ev.autoApproved === 'boolean') slim.autoApproved = ev.autoApproved;
  if (ev.skipped != null) slim.skipped = ev.skipped;
  if (ev.dismissed != null) slim.dismissed = ev.dismissed;
  if (ev.type === 'user_question_resolved' && ev.reason) slim.reason = capSlimString(String(ev.reason), SLIM_NOTE_LEN);
  if (ev.timedOut != null) slim.timedOut = ev.timedOut;
  if (ev.optionId) slim.optionId = capSlimString(String(ev.optionId), 60);
  if (ev.answer) slim.answer = capSlimString(String(ev.answer), SLIM_NOTE_LEN);
  if (ev.percent != null && Number.isFinite(Number(ev.percent))) slim.percent = Number(ev.percent);
  if (ev.roll != null && Number.isFinite(Number(ev.roll))) slim.roll = Number(ev.roll);
  if (ev.error) slim.error = capSlimString(String(ev.error), 500);
  if (Array.isArray(ev.steering)) {
    slim.steering = ev.steering.slice(-8).map((entry) => (
      typeof entry === 'string'
        ? capSlimString(entry, 200)
        : { message: capSlimString(String(entry?.message ?? ''), 200) }
    ));
  }

  const args = slimWorkArgs(ev.args || ev.arguments);
  if (args) slim.args = args;

  if (ev.type === 'message_end' && ev.message) {
    const text = piMessageText(ev.message);
    slim.message = {
      role: ev.message.role,
      content: text ? [{ type: 'text', text: capSlimString(text, SLIM_MESSAGE_LEN) }] : [],
    };
  } else if (ev.type === 'user_message' && ev.text) {
    slim.text = capSlimString(String(ev.text), SLIM_MESSAGE_LEN);
  }

  const model = ev.details?.model || ev.result?.details?.model || ev.result?.model;
  if (model) slim.details = { model: String(model) };
  const creditsUsed = creditsFromWorkEvent(ev);
  if (creditsUsed > 0) {
    slim.creditsUsed = creditsUsed;
    slim.details = { ...(slim.details || {}), creditsUsed };
  }

  if (ev.type === 'browser_login_requested') {
    if (ev.requestedUrl) slim.requestedUrl = capSlimString(String(ev.requestedUrl), SLIM_ARG_LEN);
    if (ev.reason) slim.reason = capSlimString(String(ev.reason), SLIM_NOTE_LEN);
    if (Array.isArray(ev.fields)) slim.fields = ev.fields;
  }
  if (ev.type === 'action_approval_changed') slim.actionApproval = ev.actionApproval;
  if (ev.type === 'browser_confirmation_requested') {
    if (ev.action) slim.action = capSlimString(String(ev.action), SLIM_NOTE_LEN);
    if (ev.detail) slim.detail = capSlimString(String(ev.detail), 4000);
    if (ev.sessionGrant?.label) slim.sessionGrant = { label: capSlimString(String(ev.sessionGrant.label), SLIM_NOTE_LEN) };
  }
  if (ev.type === 'user_question_requested') {
    if (typeof ev.required === 'boolean') slim.required = ev.required;
    const mediaReview = slimWorkMediaReview(ev.mediaReview);
    if (mediaReview) slim.mediaReview = mediaReview;
    if (ev.question) slim.question = capSlimString(String(ev.question), WORK_QUESTION_MAX_CHARS);
    if (ev.estimatedCredits != null) slim.estimatedCredits = ev.estimatedCredits;
    if (Array.isArray(ev.options)) slim.options = ev.options.slice(0, 8);
    if (ev.allowCustom != null) slim.allowCustom = !!ev.allowCustom;
    const questions = slimWorkQuestions(ev.questions);
    if (questions) slim.questions = questions;
    if (ev.plan) slim.plan = slimWorkPlan(ev.plan);
    if (ev.scriptPreview) slim.scriptPreview = capSlimString(String(ev.scriptPreview), WORK_SCRIPT_PREVIEW_MAX_CHARS);
  }
  if (ev.type === 'plan_updated' && ev.plan) slim.plan = slimWorkPlan(ev.plan);
  if (ev.type === 'session_end') {
    if (ev.reason) slim.reason = capSlimString(String(ev.reason), SLIM_NOTE_LEN);
    if (Array.isArray(ev.artifacts)) {
      slim.artifacts = ev.artifacts.slice(0, 50).map(slimWorkDeliverable).filter(Boolean);
    }
  }
  if (ev.type === 'artifact_ready') {
    const artifact = slimWorkDeliverable(ev.artifact || ev);
    if (artifact) slim.artifact = artifact;
  }
  if (ev.type === 'site_published' || ev.type === 'site_unpublished') {
    const site = slimPublishedSite(ev.site);
    if (site) slim.site = site;
    if (ev.type === 'site_published' && ev.redeployed) slim.redeployed = true;
  }
  if (ev.type === 'checkpoint') {
    if (ev.turn != null) slim.turn = Number(ev.turn) || 0;
    if (ev.sha) slim.sha = String(ev.sha).slice(0, 40);
    if (ev.label) slim.label = capSlimString(String(ev.label), 120);
    if (ev.changed != null) slim.changed = Number(ev.changed) || 0;
    if (Array.isArray(ev.artifacts)) slim.artifacts = ev.artifacts.slice(0, 20).map((p) => capSlimString(String(p), 200));
  }
  if (ev.type === 'subagent') {
    if (ev.taskId) slim.taskId = capSlimString(String(ev.taskId), 60);
    if (ev.label) slim.label = capSlimString(String(ev.label), 120);
    if (ev.role) slim.role = capSlimString(String(ev.role), 20);
    if (ev.model) slim.model = capSlimString(String(ev.model), 80);
    if (ev.background != null) slim.background = !!ev.background;
    if (ev.fanOut != null) slim.fanOut = Number(ev.fanOut) || 0;
    if (ev.okCount != null) slim.okCount = Number(ev.okCount) || 0;
    if (ev.partialCount != null) slim.partialCount = Number(ev.partialCount) || 0;
    if (ev.toolCalls != null) slim.toolCalls = Number(ev.toolCalls) || 0;
    if (ev.lastTool) slim.lastTool = capSlimString(String(ev.lastTool), 40);
    const toolArgs = slimWorkArgs(ev.lastToolArgs);
    if (toolArgs) slim.lastToolArgs = toolArgs;
  }
  if (ev.type === 'skill_routed') {
    if (ev.skill) slim.skill = capSlimString(String(ev.skill), 60);
    if (ev.score != null) slim.score = Number(ev.score) || 0;
    if (ev.reason) slim.reason = capSlimString(String(ev.reason), 20);
  }
  if (ev.type === 'session_note' && ev.hook) slim.hook = capSlimString(String(ev.hook), 20);

  return slim;
}

function slimWorkDeliverable(artifact) {
  if (!artifact || typeof artifact !== 'object') return null;
  const path = capSlimString(String(artifact.path || ''), 400);
  if (!path) return null;
  const out = {
    path,
    size: Number(artifact.size) || 0,
    contentType: capSlimString(String(artifact.contentType || ''), 120),
  };
  if (artifact.file) out.file = artifact.file;
  if (artifact.r2) out.r2 = artifact.r2;
  return out;
}

// Matches the execution host's ask cap. Longer asks are rejected so the
// agent retries with one or two sentences instead of flooding the card.
export const WORK_QUESTION_MAX_CHARS = 280;
export const WORK_SCRIPT_PREVIEW_MAX_CHARS = 12000;

/** Clip card copy so a long stored question cannot flood the box. */
export function workQuestionDisplayText(text) {
  const value = String(text || '').trim();
  if (!value) return '';
  if (value.length <= WORK_QUESTION_MAX_CHARS) return value;
  return `${value.slice(0, WORK_QUESTION_MAX_CHARS).trimEnd()}…`;
}

/** Collapse the blank-line runs that make a 42-clip ask_user dump look empty. */
export function compactWorkQuestionMarkdown(text) {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * react-native-markdown-display heading rules on web render native <h*> tags.
 * React does not suffix `lineHeight` with px (unitless line-height is valid
 * CSS), so `lineHeight: 21` becomes `line-height: 21` — 21× the font size,
 * which is the giant gap between Clip headings. Pass a px string on web.
 */
export function workMarkdownLineHeight(px, isWeb) {
  const n = Math.max(1, Math.round(Number(px) || 0));
  return isWeb ? `${n}px` : n;
}

/** Client abort / proxy 504: the POST may still complete on the host. */
export function isWorkAskTimeoutError(err) {
  const status = Number(err?.status);
  if (status === 0 || status === 504) return true;
  return /timed out/i.test(String(err?.message || ''));
}
const WORK_SUB_QUESTIONS_MAX = 6;

/**
 * Sub-questions of one ask_user card: [{ id, question, options }]. Present
 * when the agent asked several things at once; the card shows one at a time
 * and advances after each answer.
 */
export function slimWorkQuestions(questions) {
  if (!Array.isArray(questions) || !questions.length) return undefined;
  const out = questions.slice(0, WORK_SUB_QUESTIONS_MAX).map((q, i) => ({
    id: String(q?.id || `q-${i}`),
    question: capSlimString(String(q?.question || ''), WORK_QUESTION_MAX_CHARS),
    options: (Array.isArray(q?.options) ? q.options : []).slice(0, 8)
      .map((o) => (typeof o === 'string' ? { id: o, label: o } : o))
      .filter((o) => o?.label),
  })).filter((q) => q.question);
  return out.length ? out : undefined;
}

/**
 * One answer string for a multi-question card, in the order the questions
 * were asked, so the agent reads it the way a person would write it back.
 * Unanswered entries are named so the agent picks a default for exactly those.
 */
export function workComposeQuestionAnswers(questions, answers = {}) {
  const list = Array.isArray(questions) ? questions : [];
  const lines = list.map((q, i) => {
    const raw = answers[q.id] ?? answers[i];
    const answer = String(raw ?? '').trim();
    return `${i + 1}. ${q.question} — ${answer || '(no answer — pick a sensible default)'}`;
  });
  return lines.join('\n').slice(0, 2000);
}

/**
 * After answering (or skipping) the question at `index`, which step to show
 * next. `null` means the card should submit the collected answers.
 */
export function workAdvanceQuestionStep(total, index) {
  const n = Math.max(0, Number(total) || 0);
  const i = Math.max(0, Number(index) || 0);
  if (i + 1 >= n) return null;
  return i + 1;
}

export function workQuestionStepLabel(index, total) {
  const n = Math.max(1, Number(total) || 1);
  const i = Math.min(Math.max(0, Number(index) || 0), n - 1);
  return `QUESTION ${i + 1} OF ${n}`;
}

/**
 * True when every sub-question is still blank — Skip on the last step should
 * tell the agent the whole card was skipped, not send a list of defaults.
 */
export function workShouldSkipQuestionCard(questions, answers = {}) {
  const list = Array.isArray(questions) ? questions : [];
  if (!list.length) return true;
  return !list.some((q, i) => String(answers[q.id] ?? answers[i] ?? '').trim());
}

/**
 * Restore the option highlight vs typed draft when paging back to a step.
 */
export function workQuestionDraft(item, stored) {
  const value = String(stored ?? '').trim();
  const options = Array.isArray(item?.options) ? item.options : [];
  if (value && options.some((o) => o.label === value)) {
    return { selectedLabel: value, custom: '' };
  }
  return { selectedLabel: '', custom: value };
}

export const WORK_PLAN_STATUSES = ['pending', 'in_progress', 'done', 'blocked', 'skipped'];

export function normalizeWorkPlanStatus(value) {
  const s = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (s === 'completed' || s === 'complete' || s === 'finished' || s === 'checked') return 'done';
  if (s === 'active' || s === 'running' || s === 'current' || s === 'working' || s === 'wip' || s === 'in_progress') {
    return 'in_progress';
  }
  if (s === 'todo' || s === 'open' || s === 'not_started') return 'pending';
  return WORK_PLAN_STATUSES.includes(s) ? s : 'pending';
}

/** Preserve parallel activity; only explicit completed statuses count as done. */
export function settleWorkPlanSteps(steps) {
  const list = Array.isArray(steps) ? steps : [];
  let statusChanged = false;
  const normalized = list.map((step) => {
    const status = normalizeWorkPlanStatus(step?.status);
    if (status !== step?.status) statusChanged = true;
    return status === step?.status ? step : { ...step, status };
  });
  return statusChanged ? normalized : list;
}

export function settleWorkPlan(plan) {
  if (!plan || typeof plan !== 'object') return plan;
  const steps = settleWorkPlanSteps(plan.steps);
  if (steps === plan.steps) return plan;
  return { ...plan, steps };
}

/**
 * The update_plan checklist as the event log carries it: goal, class, up to
 * 20 short steps. Shared by plan_updated and the plan-approval question.
 */
export function slimWorkPlan(plan) {
  if (!plan || typeof plan !== 'object') return null;
  const steps = settleWorkPlanSteps((Array.isArray(plan.steps) ? plan.steps : []).slice(0, 20).map((s) => {
    const out = {
      id: String(s?.id || ''),
      title: capSlimString(String(s?.title || ''), 160),
      status: normalizeWorkPlanStatus(s?.status),
    };
    if (s?.note) out.note = capSlimString(String(s.note), 160);
    return out;
  }).filter((s) => s.title));
  return {
    goal: capSlimString(String(plan.goal || ''), 300),
    classification: String(plan.classification || ''),
    steps,
    revision: Number(plan.revision) || 1,
    approved: !!plan.approved,
  };
}

/** Latest published plan, or a store-retained fallback after the event cap. */
export function workPlanFromEvents(events, retainedPlan = null) {
  let plan = retainedPlan;
  for (const ev of Array.isArray(events) ? events : []) {
    if ((ev?.type === 'plan_updated' || (ev?.type === 'user_question_requested' && ev.kind === 'plan')) && ev.plan) {
      plan = ev.plan;
    }
  }
  return settleWorkPlan(plan);
}

/**
 * Which checkpoint turn a transcript message belongs to: the orchestrator
 * numbers turns by user_message events, so count user turns up to (and
 * including) this message. Queued follow-ups are not turns yet.
 * Returns { turn, totalTurns, hasDeliverable }.
 */
export function workTurnIndexForMessage(messages, messageId, { isQueuedMessage } = {}) {
  const list = Array.isArray(messages) ? messages : [];
  let turn = 0;
  let total = 0;
  let found = false;
  for (const m of list) {
    const isUser = m?.role === 'user';
    if (isUser && !(isQueuedMessage && isQueuedMessage(m))) total += 1;
    if (!found) {
      if (isUser && !(isQueuedMessage && isQueuedMessage(m))) turn = total;
      if (String(m?.id || m?._id || '') === String(messageId)) found = true;
    }
  }
  return { turn: found ? turn : 0, totalTurns: total };
}

/**
 * Checkpoint controls belong to one transcript row per user turn: the last
 * assistant row before the next user message. Supports Mongo `_id` messages.
 */
export function workTurnActionMeta(messages, messageId, { isQueuedMessage } = {}) {
  const list = Array.isArray(messages) ? messages : [];
  const wanted = String(messageId || '');
  const index = list.findIndex((message) => (
    String(message?.id || message?._id || '') === wanted
  ));
  const indexed = workTurnIndexForMessage(list, wanted, { isQueuedMessage });
  if (index < 0 || list[index]?.role !== 'assistant' || indexed.turn < 1) {
    return { ...indexed, shouldRender: false };
  }
  for (let i = index + 1; i < list.length; i += 1) {
    const message = list[i];
    if (message?.role === 'user' && !(isQueuedMessage && isQueuedMessage(message))) break;
    if (message?.role === 'assistant') return { ...indexed, shouldRender: false };
  }
  return { ...indexed, shouldRender: true };
}

/** Resolve a restore button to a real checkpoint SHA, including undo. */
export function workCheckpointRestoreTarget(checkpoints, turn, totalTurns) {
  const list = (Array.isArray(checkpoints) ? checkpoints : [])
    .filter((checkpoint) => checkpoint?.sha);
  if (!list.length || !(Number(turn) > 0)) return null;
  const current = list[list.length - 1];
  if (current.kind === 'restore' && Number(current.turn) === Number(turn)) return null;

  if (Number(turn) === Number(totalTurns) && current.kind === 'restore') {
    for (let i = list.length - 2; i >= 0; i -= 1) {
      if (list[i].kind === 'pre-restore') {
        return { ...list[i], isUndo: true };
      }
      if (list[i].kind === 'restore') break;
    }
    for (let i = list.length - 1; i >= 0; i -= 1) {
      if (list[i].kind === 'turn' && Number(list[i].turn) === Number(turn)) {
        return { ...list[i], isUndo: true };
      }
    }
  }

  for (let i = list.length - 1; i >= 0; i -= 1) {
    const checkpoint = list[i];
    if (checkpoint.kind === 'turn' && Number(checkpoint.turn) === Number(turn)) {
      if (checkpoint.sha === current.sha) return null;
      return checkpoint;
    }
  }
  return null;
}

export function workPlanProgress(plan) {
  const steps = settleWorkPlanSteps(plan?.steps);
  const done = steps.filter((s) => s.status === 'done' || s.status === 'skipped').length;
  return { done, total: steps.length, allDone: steps.length > 0 && done === steps.length };
}

/** Compact status on the pinned plan card: "2/5", "Done", or "Needs your OK". */
export function workPlanStatusLabel(plan, { awaitingApproval = false } = {}) {
  if (awaitingApproval) return 'Needs your OK';
  const progress = workPlanProgress(plan);
  if (!progress.total) return '';
  if (progress.allDone) return 'Done';
  return `${progress.done}/${progress.total}`;
}

/**
 * The plan card stays attached to the conversation like a file — not only
 * while the turn that published it is live. The approval gate renders the
 * same card in the composer, so the transcript pin hides for that moment.
 */
export function shouldShowWorkPinnedPlan({ plan = null, questionKind = null } = {}) {
  if (questionKind === 'plan') return false;
  return workPlanProgress(plan).total > 0;
}

/**
 * Make a plan decision explicit in both fields. Prefixing revision requests
 * with "Changes" prevents approval-like free text ("looks good, but…") from
 * being classified as approval by older execution hosts.
 */
export function workPlanDecisionAnswer(decision, text = '') {
  if (decision === 'approve') {
    return { answer: 'Approve plan', optionId: 'approve' };
  }
  const detail = String(text || '').trim();
  return {
    answer: detail ? `Changes requested: ${detail}` : 'Changes requested: Please revise the plan.',
    optionId: 'changes',
  };
}

export function isFatWorkEvent(ev) {
  return !!(ev && (ev.result != null || ev.content != null || ev.assistantMessageEvent || ev.arguments));
}

function piMessageThinking(message) {
  if (typeof message?.reasoning_content === 'string' && message.reasoning_content.trim()) {
    return message.reasoning_content.trim();
  }
  return (message?.content || [])
    .filter((c) => (c.type === 'thinking' || c.type === 'reasoning') && typeof c.text === 'string')
    .map((c) => c.text)
    .join('\n')
    .trim();
}

/**
 * Reduce a Pi message_start / message_update / message_end into the live
 * assistant draft. Token deltas never hit the persisted event log.
 */
export function applyWorkStreamDelta(state, ev) {
  const prev = {
    content: String(state?.content || ''),
    thinking: String(state?.thinking || ''),
    isThinking: !!state?.isThinking,
    open: !!state?.open,
  };
  if (ev?.type === 'message_start') {
    if (ev.message?.role && ev.message.role !== 'assistant') return state || prev;
    return { content: '', thinking: '', isThinking: false, open: true };
  }
  if (ev?.type === 'message_update') {
    const delta = ev.assistantMessageEvent || {};
    switch (delta.type) {
      case 'text_delta':
        return {
          ...prev,
          open: true,
          isThinking: false,
          content: prev.content + String(delta.delta || ''),
        };
      case 'text_end':
        return {
          ...prev,
          open: true,
          isThinking: false,
          content: typeof delta.content === 'string' ? delta.content : prev.content,
        };
      case 'thinking_start':
        return { ...prev, open: true, isThinking: true };
      case 'thinking_delta':
        return {
          ...prev,
          open: true,
          isThinking: true,
          thinking: prev.thinking + String(delta.delta || ''),
        };
      case 'thinking_end':
        return { ...prev, open: true, isThinking: false };
      default:
        return { ...prev, open: true };
    }
  }
  if (ev?.type === 'message_end' && (!ev.message?.role || ev.message.role === 'assistant')) {
    const ended = piMessageText(ev.message);
    const thinking = piMessageThinking(ev.message);
    // A slimmed message_end (event-log cap) must not replace a longer
    // live draft. Prefer the finished text when it is at least as long.
    return {
      content: (ended && ended.length >= prev.content.length) ? ended : (prev.content || ended),
      thinking: (thinking && thinking.length >= prev.thinking.length) ? thinking : (prev.thinking || thinking),
      isThinking: false,
      open: false,
    };
  }
  return prev;
}

/**
 * Pending login / confirmation / ask_user cards from the event log.
 * Approval cards end the turn (waiting_on_user) instead of polling. A resume
 * or new start drops the box; waiting_on_user keeps it until they answer.
 */
function isHoldableWorkQuestion(question) {
  const kind = String(question?.kind || 'choice');
  if (kind === 'credits' || kind === 'cost' || kind === 'memory' || kind === 'plan') return false;
  return !!(question && (question.question || question.options?.length || question.questions?.length));
}

export function workPendingInteractionFromEvents(events, options) {
  let loginRequest = null;
  let confirmation = null;
  let question = null;
  let heldScriptQuestion = null;
  const resolvedIds = options?.resolvedIds;
  for (const ev of Array.isArray(events) ? events : []) {
    // A submitted new direction supersedes the old question. Draft typing
    // emits no user_message, so it must never dismiss the card.
    if (ev?.type === 'user_message') {
      question = null;
      heldScriptQuestion = null;
      continue;
    }
    if (question?.required === false && ['message_end', 'session_end'].includes(ev?.type)) {
      question = null;
      heldScriptQuestion = null;
    }
    if (ev?.type === 'session_start' || ev?.type === 'session_resume') {
      loginRequest = null;
      confirmation = null;
      question = null;
      heldScriptQuestion = null;
      continue;
    }
    // A completed turn that asked the user something must keep the card.
    // Questions remain answerable after failure/restart as well as a normal
    // handoff. Only the user may answer, skip, stop or replace a question.
    if (ev?.type === 'session_end') {
      // Explicit Stop cancels the request, including a choice preserved
      // after its waiter was dismissed. Keep resumable failure/pause cards
      // below, but never revive an approval the user just stopped.
      if (ev.status === 'stopped') {
        loginRequest = null;
        confirmation = null;
        question = null;
        heldScriptQuestion = null;
        continue;
      }
      const keep = ev.status === 'completed' || ev.reason === 'waiting_on_user';
      if (!keep) {
        loginRequest = null;
        confirmation = null;
      }
      continue;
    }
    if (ev?.type === 'browser_login_requested') loginRequest = ev;
    else if (ev?.type === 'browser_login_complete' || ev?.type === 'browser_login_timeout' || ev?.type === 'browser_login_skipped') loginRequest = null;
    else if (ev?.type === 'browser_confirmation_requested') confirmation = ev;
    else if (ev?.type === 'browser_confirmation_resolved') confirmation = null;
    else if (ev?.type === 'user_question_requested') {
      question = ev;
      if (isHoldableWorkQuestion(ev)) heldScriptQuestion = ev;
    } else if (ev?.type === 'user_question_resolved') {
      // Concurrent calls can resolve an older question after a newer card
      // appears. Only its own answer/timeout may dismiss the current card.
      const currentId = question?.id || heldScriptQuestion?.id;
      if (ev.id && currentId && ev.id !== currentId) continue;
      // A new user direction (including approval in Video studio) replaces
      // the old card. Only preserve waiters lost to interruption, not those
      // deliberately superseded. Missing reasons retain legacy recovery.
      const preserveDismissed = ev.dismissed && (!ev.reason || ev.reason === 'session_ended');
      if (preserveDismissed && isHoldableWorkQuestion(question || heldScriptQuestion)) {
        heldScriptQuestion = { ...(question || heldScriptQuestion), orphaned: true };
      } else {
        heldScriptQuestion = null;
      }
      question = null;
    }
  }
  if (!question && heldScriptQuestion) {
    question = heldScriptQuestion.orphaned
      ? heldScriptQuestion
      : { ...heldScriptQuestion, orphaned: true };
  }
  // A tap must hide the card before /ask returns — otherwise a slow host
  // leaves Approve spinning on "Waiting for your approval".
  if (resolvedIds && (Array.isArray(resolvedIds) ? resolvedIds.length : resolvedIds.size)) {
    const hidden = resolvedIds instanceof Set ? resolvedIds : new Set(resolvedIds);
    if (question?.id && hidden.has(question.id)) question = null;
    if (confirmation?.id && hidden.has(confirmation.id)) confirmation = null;
  }
  return { loginRequest, confirmation, question };
}

export function workLifecycleStatusFromEvents(events) {
  let status = null;
  for (const ev of Array.isArray(events) ? events : []) {
    if (ev?.type === 'session_start' || ev?.type === 'session_resume') status = 'running';
    else if (ev?.type === 'session_end') {
      status = ev.status === 'completed' ? 'idle' : (ev.status || 'idle');
    }
  }
  return status;
}

/**
 * First-paint status for a Work thread we already know about. Leaving and
 * coming back used to start at `null` (settled) until fetchTask returned,
 * which folded the live trail and hid the working indicator. A stored
 * settled status wins over leftover events; otherwise the event log and
 * the chat's inferencing flag keep the spinner up.
 */
export function seedWorkTaskStatus({ task, events, isInferencing } = {}) {
  const mapped = task?.status === 'completed' ? 'idle' : (task?.status || null);
  if (mapped && !isWorkActive(mapped)) return mapped;
  if (isWorkActive(mapped)) return mapped;
  const life = workLifecycleStatusFromEvents(events);
  if (life === 'running') return 'running';
  if (mapped) return mapped;
  if (isInferencing) return 'running';
  return null;
}

/**
 * Whether the UI should show a working indicator. The event log wins when
 * it still has an open session — a stale idle taskStatus after the first
 * assistant comment used to fold the trail and hide the spinner.
 */
export function workIsActivelyRunning({ taskStatus, task, events, isInferencing } = {}) {
  // A confirmed Stop must clear the composer immediately, even while the
  // event stream still ends at session_resume (for example during compaction).
  // A new follow-up explicitly switches taskStatus back to running.
  if (taskStatus === 'stopped') return false;
  if (workLifecycleStatusFromEvents(events) === 'running') return true;
  if (isWorkActive(taskStatus)) return true;
  const mapped = task?.status === 'completed' ? 'idle' : (task?.status || null);
  // A fresh host/SSE status must beat a task-summary object that has not
  // caught up yet. Otherwise a finished run keeps Stop and its spinner.
  if (taskStatus && !isWorkActive(taskStatus)) return false;
  if (isWorkActive(mapped)) return true;
  if (mapped && !isWorkActive(mapped)) return false;
  return !!isInferencing;
}

function workHasInFlightTool(events) {
  let inflight = false;
  for (const ev of Array.isArray(events) ? events : []) {
    if (ev?.type === 'session_start' || ev?.type === 'session_resume') {
      inflight = false;
      continue;
    }
    if (ev?.type === 'tool_execution_start' && !isPlatformMediaFetch(ev)) inflight = true;
    else if (ev?.type === 'tool_execution_end' && !isPlatformMediaFetch(ev)) inflight = false;
  }
  return inflight;
}

function workDeliveredSinceLastUser(events) {
  let delivered = false;
  for (const ev of Array.isArray(events) ? events : []) {
    if (ev?.type === 'user_message') delivered = false;
    else if (ev?.type === 'artifact_ready') delivered = true;
  }
  return delivered;
}

/**
 * The live status row is the only "I am still working" signal in the
 * transcript. Growing reply text and a finished comment are not substitutes
 * — tokens pause for long stretches between tools, leftover isStreaming on
 * a draft hid every spinner, and an under-reply line is easy to skip
 * (grouped rows, hidden drafts). Show this whenever the run is live, except
 * after this turn already exported a file and wrote a real reply: remote
 * orch can sit idle for minutes, and a thinking clock on a finished Word
 * card looks broken.
 */
export function shouldShowWorkLiveActivity({
  isActive = false,
  hasInferencingWaiting = false,
  events,
  lastAssistantText,
} = {}) {
  if (!isActive || hasInferencingWaiting) return false;
  if (workHasInFlightTool(events)) return true;
  if (
    workDeliveredSinceLastUser(events)
    && looksLikeWorkAnswer(lastAssistantText)
    && !isWorkFillerNarration(lastAssistantText)
  ) {
    return false;
  }
  return true;
}

function workEventSeq(ev) {
  return Number(ev?.seq) || 0;
}

function isWorkPlanPublishEvent(ev) {
  return !!(ev && ev.plan && (
    ev.type === 'plan_updated'
    || (ev.type === 'user_question_requested' && ev.kind === 'plan')
  ));
}

function isWorkTranscriptUserRow(row, isQueuedMessage) {
  return row?.role === 'user' && !(isQueuedMessage && isQueuedMessage(row));
}

/**
 * Where to splice the pinned plan card: directly under the prompt that
 * published it. A follow-up does not drag a finished plan under the new
 * bubble; a new `plan_updated` moves the pin to that turn.
 */
export function workPlanAttachIndex(rows, events, { isQueuedMessage, plan = null } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  let users = 0;
  let usersAtLastSession = 0;
  let userTurnsAtPlan = 0;
  let lastSessionSeq = -1;
  let lastPlanSeq = -1;
  let sawPlan = false;
  for (const ev of Array.isArray(events) ? events : []) {
    const seq = workEventSeq(ev);
    if (ev?.type === 'session_start' || ev?.type === 'session_resume') {
      lastSessionSeq = seq;
      usersAtLastSession = users;
    }
    if (ev?.type === 'user_message') users += 1;
    if (isWorkPlanPublishEvent(ev)) {
      lastPlanSeq = seq;
      userTurnsAtPlan = users;
      sawPlan = true;
    }
  }
  if (!sawPlan) {
    const followUpWithoutPlan = lastSessionSeq > lastPlanSeq && users > usersAtLastSession;
    userTurnsAtPlan = (workPlanProgress(plan).allDone && followUpWithoutPlan && usersAtLastSession > 0)
      ? usersAtLastSession
      : users;
  }

  const attachBeforeLiveRows = () => {
    let i = list.length;
    while (i > 0 && (list[i - 1]?.type === 'work-live-activity' || list[i - 1]?.type === 'work-plan')) i -= 1;
    return i;
  };

  if (!(userTurnsAtPlan > 0)) return attachBeforeLiveRows();

  let seenUsers = 0;
  for (let i = 0; i < list.length; i += 1) {
    if (!isWorkTranscriptUserRow(list[i], isQueuedMessage)) continue;
    seenUsers += 1;
    if (seenUsers !== userTurnsAtPlan) continue;
    let insertAt = i + 1;
    while (insertAt < list.length) {
      const next = list[insertAt];
      if (next?.type !== 'work-upgrade-notice' && next?.type !== 'date-separator') break;
      insertAt += 1;
    }
    return insertAt;
  }
  return attachBeforeLiveRows();
}

function toolArgs(ev) {
  const raw = ev?.args || ev?.arguments;
  return (raw && typeof raw === 'object') ? raw : {};
}

function workDelegatedTaskLabel(args) {
  const label = String(args?.label || args?.title || args?.goal || '').replace(/\s+/g, ' ').trim();
  if (label) return truncate(label, 60);
  const prompt = String(args?.prompt || args?.description || '').replace(/\s+/g, ' ').trim();
  if (prompt) {
    const clause = prompt.split(/[.?!]/)[0].trim();
    if (clause.length >= 12) return truncate(clause, 60);
  }
  return '';
}

function workDelegatedTaskText(args) {
  const label = workDelegatedTaskLabel(args);
  if (label) return `Delegating: ${label}`;
  const agent = String(args?.agent || '').toLowerCase();
  if (agent === 'explore') return 'Investigating in the background';
  if (args?.background) return 'Running research in the background';
  return 'Delegating part of the work';
}

/**
 * Plain-language activity for one Work tool call. Names the model, skill, or
 * action; never dumps shell commands, grep patterns, or sandbox paths.
 */
export function describeWorkTool(ev) {
  const name = String(ev?.toolName || ev?.name || '').toLowerCase();
  const args = toolArgs(ev);
  const path = args.path || args.filename || args.file || args.file_path || args.filePath
    || args.target_file || args.targetFile || '';
  const skill = skillSlugFromPath(path);

  if (name === 'mac_desktop') {
    const surface = args.surface === 'browser' ? 'local browser' : 'selected Mac app';
    const labels = { apps: 'Checking approved Mac apps', activate: 'Opening the selected Mac app', status: 'Checking the local browser',
      snapshot: `Reading the ${surface}`, screenshot: `Viewing the ${surface}`, click: `Clicking in the ${surface}`,
      type: `Entering text in the ${surface}`, key: `Using the ${surface}`, scroll: `Scrolling the ${surface}`,
      open: 'Opening a page in the local browser', back: 'Going back in the local browser', forward: 'Going forward in the local browser', reload: 'Reloading the local browser' };
    return { kind: 'tool', name, icon: 'laptop', text: labels[args.operation] || 'Using your Mac' };
  }

  if (['site_names', 'publish_site', 'site_status', 'unpublish_site'].includes(name)) {
    const site = typeof args.name === 'string' && /^[a-z0-9-]{3,40}$/.test(args.name) ? args.name : '';
    const text = {
      site_names: 'Checking site names',
      publish_site: site ? `Publishing ${site}.${SITES_DOMAIN}` : 'Publishing the site',
      site_status: 'Checking published sites',
      unpublish_site: site ? `Unpublishing ${site}` : 'Unpublishing the site',
    }[name];
    return { kind: 'tool', name, icon: 'globe', text };
  }

  if (name === 'project_files') {
    const file = userFacingFileName(path);
    const item = file || 'files';
    const files = Array.isArray(args.paths) ? args.paths.slice(0, 8).map(userFacingFileName).filter(Boolean) : [];
    const batch = files.length ? `${files.length} ${files.length === 1 ? 'file' : 'files'}: ${files.slice(0, 2).join(', ')}${files.length > 2 ? ', …' : ''}` : 'a batch of files';
    const labels = {
      list: 'Looking through your Mac folder',
      search: 'Searching your Mac folder',
      overview: 'Mapping your Mac folder',
      map: 'Finding relevant files and symbols on your Mac',
      read_many: `Reading ${batch} on your Mac`,
      git_status: 'Checking changed files on your Mac',
      git_diff: `Reviewing changes to ${item} on your Mac`,
      stat: `Checking ${item} on your Mac`,
      read: `Reading ${item} on your Mac`,
      import: `Importing ${item} from your Mac`,
      edit: `Editing ${item} on your Mac`,
      write: `Writing ${item} on your Mac`,
      save: `Saving ${item} to your Mac`,
      mkdir: 'Creating a folder on your Mac',
      move: `Moving ${item} on your Mac`,
      rename: `Renaming ${item} on your Mac`,
      copy: `Copying ${item} on your Mac`,
      trash: `Moving ${item} to Finder Trash`,
    };
    return { kind: 'tool', name, icon: 'folder-open', text: labels[args.operation] || 'Working in your Mac folder' };
  }

  if (skill) {
    const slug = String(skill).toLowerCase();
    const text = slug === 'using-skills'
      ? 'Choosing an approach'
      : `Using the ${skillTitle(skill)} skill`;
    return {
      kind: 'skill',
      name: 'skill',
      icon: 'book',
      text,
    };
  }

  if (name === 'run_model') {
    const model = friendlyWorkModelName(args.model);
    const slug = String(args.model || '').toLowerCase();
    const prompt = String(args.prompt || args.query || args.input || '');
    // Arya is the default Standard run_model slug. Naming every consult
    // "Asking Arya" on an icon/video job makes it look like Arya drew the
    // picture. Only credit that consult when the prompt is actually a
    // political / theological ask — not media production.
    const mediaPrompt = /\b(image|icon|logo|video|clip|picture|photo|poster|thumbnail|3d|song|music|illustration)\b/i.test(prompt);
    const politicalPrompt = /\b(political|theolog|bible|christian|gospel|scripture|election|immigrat|reformation|sermon|abortion|border|zionis|israel|hamas|marx|capitalis|conservative|liberal)\b/i.test(prompt);
    const scriptPrompt = /\b(script|screenplay|narration)\b/i.test(prompt) && /\b(write|draft|revise|rewrite)\b/i.test(prompt);
    const nameConsult = model && (slug !== 'arya' || (politicalPrompt && !mediaPrompt));
    return {
      kind: 'model',
      name: 'run_model',
      icon: 'comments',
      text: scriptPrompt && model ? `Writing with ${model}` : nameConsult ? `Asking ${model}` : 'Thinking',
      model: args.model || '',
    };
  }

  if (name === 'list_models') {
    return { kind: 'model', name: 'list_models', icon: 'comments', text: 'Looking up models' };
  }

  if (name === 'run_agent') {
    const agent = String(args.agentName || args.name || '').trim();
    return {
      kind: 'agent',
      name: 'run_agent',
      icon: 'robot',
      text: agent ? `Working with ${agent}` : 'Working with an agent',
      agent,
    };
  }

  if (name === 'list_agents') {
    return { kind: 'agent', name: 'list_agents', icon: 'robot', text: 'Looking up agents' };
  }

  if (name === 'task') {
    return { kind: 'tool', name: 'task', icon: 'sitemap', text: workDelegatedTaskText(args) };
  }

  if (name === 'task_status') {
    return {
      kind: 'tool',
      name: 'task_status',
      icon: 'sitemap',
      text: args.wait ? 'Waiting for delegated work to finish' : 'Checking on delegated work',
    };
  }

  if (name === 'update_plan') {
    return { kind: 'tool', name: 'update_plan', icon: 'list-check', text: 'Updating the plan' };
  }

  if (name === 'bash') {
    return { kind: 'tool', name: 'bash', icon: 'desktop', text: 'Working in the workspace' };
  }

  if (name === 'grep' || name === 'glob') {
    return { kind: 'tool', name, icon: 'folder-open', text: 'Looking through files' };
  }

  if (name === 'read') {
    const file = userFacingFileName(path);
    const base = String(path || '').replace(/\\/g, '/').split('/').pop() || '';
    const savedPage = /read_url|fetch_url|tool-output/i.test(String(path || ''));
    if (!file && isWorkScratchFileName(base) && !savedPage) {
      return { kind: 'tool', name: 'read', icon: 'folder-open', text: '', hide: true };
    }
    return {
      kind: 'tool',
      name: 'read',
      icon: 'folder-open',
      text: file ? `Reading ${file}` : (savedPage ? 'Reading a saved page' : 'Reading a file'),
    };
  }

  if (name === 'write' || name === 'edit') {
    const file = userFacingFileName(path);
    const verb = name === 'write' ? 'Writing' : 'Editing';
    return {
      kind: 'tool',
      name,
      icon: 'pen',
      text: file ? `${verb} ${file}` : (name === 'write' ? 'Writing a file' : 'Editing a file'),
    };
  }

  if (name === 'web_research' || name === 'web_search') {
    const q = quotedSnippet(args.query);
    return {
      kind: 'tool',
      name,
      icon: 'globe',
      text: q ? `Searching the web for ${q}` : 'Searching the web',
    };
  }

  if (name === 'places_search') {
    const q = quotedSnippet(args.query);
    return {
      kind: 'tool',
      name,
      icon: 'location-dot',
      text: q ? `Looking up places for ${q}` : 'Looking up places',
    };
  }

  if (name === 'read_url' || name === 'fetch_url') {
    const host = workHostOf(args.url);
    const label = name === 'fetch_url' ? 'Downloading a file' : 'Reading a page';
    return {
      kind: 'tool',
      name,
      icon: 'file-lines',
      text: host ? `${label} on ${host}` : label,
    };
  }

  if (name === 'browser_navigate') {
    const host = workHostOf(args.url);
    return {
      kind: 'tool',
      name,
      icon: 'window-maximize',
      text: host ? `Opening ${host}` : 'Opening a page',
    };
  }

  if (name === 'convert_document') {
    const format = String(args.format || '').toLowerCase();
    const pretty = { pdf: 'PDF', docx: 'Word', xlsx: 'a spreadsheet', pptx: 'a presentation' }[format];
    const src = String(args.markdown_file || args.markdownFile || '').split(/[/\\]/).pop();
    const dest = pretty ? `to ${pretty}` : 'a document';
    return {
      kind: 'tool',
      name,
      icon: 'file-import',
      text: src ? `Converting ${src} ${dest}` : (pretty ? `Converting to ${pretty}` : 'Converting a document'),
    };
  }

  if (name === 'preview_document') {
    const file = String(args.filename || args.path || '').toLowerCase();
    const kind = file.endsWith('.pptx') || file.endsWith('.ppt')
      ? 'the slides'
      : file.endsWith('.docx') || file.endsWith('.doc')
        ? 'the document'
        : 'the PDF';
    return {
      kind: 'tool',
      name,
      icon: 'eye',
      text: `Previewing ${kind}`,
    };
  }

  if (name === 'generate_image') {
    const q = quotedSnippet(args.prompt, 40);
    const modelSlug = workMediaModelSlug(name, args.model);
    const model = friendlyWorkModelName(modelSlug);
    const withModel = model ? ` with ${model}` : '';
    return {
      kind: 'tool',
      name,
      icon: 'image',
      text: q ? `Generating an image of ${q}${withModel}` : `Generating an image${withModel}`,
      model: modelSlug,
    };
  }

  if (name === 'edit_image') {
    const q = quotedSnippet(args.prompt, 40);
    const modelSlug = workMediaModelSlug(name, args.model);
    const model = friendlyWorkModelName(modelSlug);
    const withModel = model ? ` with ${model}` : '';
    return {
      kind: 'tool',
      name,
      icon: 'image',
      text: q ? `Editing an image: ${q}${withModel}` : `Editing an image${withModel}`,
      model: modelSlug,
    };
  }

  if (name === 'analyze_image') {
    const model = friendlyWorkModelName(args.model);
    return {
      kind: 'tool',
      name,
      icon: 'image',
      text: model ? `Looking at an image with ${model}` : 'Looking at an image',
      model: args.model || '',
    };
  }

  if (name === 'upscale_image' || name === 'remove_background' || name === 'vectorize_image') {
    const labels = {
      upscale_image: 'Upscaling an image',
      remove_background: 'Removing the background',
      vectorize_image: 'Vectorizing an image',
    };
    const model = friendlyWorkModelName(args.model);
    const withModel = model ? ` with ${model}` : '';
    return {
      kind: 'tool',
      name,
      icon: 'image',
      text: `${labels[name]}${withModel}`,
      model: args.model || '',
    };
  }

  if (name === 'generate_video') {
    const model = friendlyWorkModelName(args.model);
    return {
      kind: 'tool',
      name,
      icon: 'image',
      text: (model ? `Generating a video with ${model}` : 'Generating a video') + clipProgressSuffix(args),
      model: args.model || '',
    };
  }

  if (name === 'image_to_video') {
    const model = friendlyWorkModelName(args.model);
    return {
      kind: 'tool',
      name,
      icon: 'image',
      text: (model ? `Animating an image with ${model}` : 'Animating an image') + clipProgressSuffix(args),
      model: args.model || '',
    };
  }

  if (name === 'extend_video') {
    const model = friendlyWorkModelName(args.model);
    return {
      kind: 'tool',
      name,
      icon: 'image',
      text: (model ? `Extending the clip with ${model}` : 'Extending the clip for character continuity') + clipProgressSuffix(args),
      model: args.model || '',
    };
  }

  if (name === 'generate_3d') {
    const q = quotedSnippet(args.prompt, 40);
    const modelSlug = workMediaModelSlug(name, args.model);
    const model = friendlyWorkModelName(modelSlug);
    const withModel = model ? ` with ${model}` : '';
    return {
      kind: 'tool',
      name,
      icon: 'cube',
      text: q ? `Generating a 3D model of ${q}${withModel}` : `Generating a 3D model${withModel}`,
      model: modelSlug,
    };
  }

  if (name === 'image_to_3d') {
    const modelSlug = workMediaModelSlug(name, args.model);
    const model = friendlyWorkModelName(modelSlug);
    return {
      kind: 'tool',
      name,
      icon: 'cube',
      text: model ? `Turning an image into 3D with ${model}` : 'Turning an image into 3D',
      model: modelSlug,
    };
  }

  if (name === 'threed_status') {
    return {
      kind: 'tool',
      name,
      icon: 'cube',
      text: 'Checking the 3D job',
      model: workMediaModelSlug(name, args.model),
    };
  }

  if (name === 'generate_music') {
    const q = quotedSnippet(args.prompt, 40);
    const model = friendlyWorkModelName(args.model);
    const withModel = model ? ` with ${model}` : '';
    return {
      kind: 'tool',
      name,
      icon: 'music',
      text: q ? `Generating a song: ${q}${withModel}` : `Generating a song${withModel}`,
      model: args.model || '',
    };
  }

  if (name === 'generate_sound_effect') {
    const q = quotedSnippet(args.prompt, 40);
    const model = friendlyWorkModelName(args.model);
    const withModel = model ? ` with ${model}` : '';
    return {
      kind: 'tool',
      name,
      icon: 'music',
      text: q ? `Generating a sound effect: ${q}${withModel}` : `Generating a sound effect${withModel}`,
      model: args.model || '',
    };
  }

  if (name === 'music_status') {
    return {
      kind: 'tool',
      name,
      icon: 'music',
      text: 'Checking the audio job',
    };
  }

  if (name === 'text_to_speech') {
    const model = friendlyWorkModelName(args.model);
    return {
      kind: 'tool',
      name,
      icon: 'music',
      text: model ? `Generating spoken audio with ${model}` : 'Generating spoken audio',
      model: args.model || '',
    };
  }

  if (name === 'transcribe_audio') {
    return {
      kind: 'tool',
      name,
      icon: 'file-audio',
      text: 'Transcribing audio',
    };
  }

  const group = GROUP_BY_TOOL.get(name);
  const label = TOOL_LABELS[name] || (name ? String(ev?.toolName || name).replace(/_/g, ' ') : WORK_THINKING_STATUS);
  return {
    kind: 'tool',
    name: name || 'tool',
    icon: group?.icon || 'arrow-right',
    text: label,
  };
}

function argsFromLegacyDetail(name, detail) {
  const extra = String(detail || '').trim();
  if (!extra) return {};
  const key = String(name || '').toLowerCase();
  if (key === 'run_model') return { model: extra };
  if (key === 'run_agent') return { agentName: extra };
  if (extra.startsWith('"') && extra.endsWith('"')) return { query: extra.slice(1, -1) };
  if (/^https?:\/\//i.test(extra)) return { url: extra };
  if (key === 'bash') return { command: extra };
  return { path: extra, filename: extra, query: extra };
}

export function humanizeWorkTool(name, detail) {
  return describeWorkTool({ toolName: name, args: argsFromLegacyDetail(name, detail) }).text;
}

export function isGenericWorkStatus(status) {
  const value = String(status || '').trim();
  if (!value) return true;
  if (GENERIC_STATUS.test(value)) return true;
  // Host activityStatus often echoes our own thinking/planning line after
  // convert/export. Treat those as empty so the last-tool wrap-up wins
  // ("Preparing the file for review") instead of a 40s thinking spinner.
  if (value === WORK_THINKING_STATUS || value === WORK_PLANNING_STATUS) return true;
  return false;
}

const WORK_TOOL_WRAP_STATUS = {
  project_files: 'Reviewing the Mac folder result',
  convert_document: 'Finishing the conversion',
  extract_document: 'Finishing the document',
  preview_document: 'Checking the document',
  export_artifact: 'Preparing the file for review',
  write: 'Saving what I wrote',
  edit: 'Saving the edit',
  fetch_url: 'Opening the file',
  read_url: 'Finishing the page',
  web_research: 'Reviewing what I found',
  web_search: 'Reviewing search results',
  run_model: 'Reviewing the answer',
  run_agent: 'Reviewing the specialist',
  bash: 'Finishing workspace work',
  read: 'Reviewing the file',
  generate_image: 'Saving the image',
  task: 'Waiting on delegated work',
  task_status: 'Collecting delegated results',
  generate_3d: 'Waiting on the 3D model',
  generate_video: 'Waiting on the video',
  collect_video_jobs: 'Reviewing video results',
  video_status: 'Reviewing video progress',
  generate_music: 'Waiting on the track',
  generate_sound_effect: 'Waiting on the sound',
};

function workToolWrapStatus(ev) {
  const name = String(ev?.toolName || ev?.name || '').toLowerCase();
  return WORK_TOOL_WRAP_STATUS[name] || WORK_THINKING_STATUS;
}

const ACTIVITY_ICONS = {
  boot: 'desktop',
  think: 'lightbulb',
  wait: 'clock',
};

/**
 * Live activity for a Work thread. Tool events win (searching the web,
 * asking a named model, using a skill). While the sandbox is coming up we
 * say we are booting the computer. Between tools we use the intent-engine
 * status phrase when it is specific, never a generic "Working".
 */
export function workActivityMetaFromEvents(events, {
  llmStatus,
  taskStatus,
  browserState,
} = {}) {
  if (browserState?.loginRequest) {
    return { text: 'Waiting for you to sign in', icon: ACTIVITY_ICONS.wait, kind: 'wait' };
  }
  if (browserState?.question) {
    return {
      text: browserState.question.kind === 'credits'
        ? 'Waiting on credits'
        : (browserState.question.kind === 'cost'
          ? 'Waiting for your approval'
          : (browserState.question.kind === 'memory'
            ? 'Waiting for your approval'
            : (browserState.question.kind === 'plan'
              ? 'Waiting for you to approve the plan'
              : 'Waiting for your answer'))),
      icon: ACTIVITY_ICONS.wait,
      kind: 'wait',
    };
  }
  if (browserState?.confirmation) {
    return { text: 'Waiting for your approval', icon: ACTIVITY_ICONS.wait, kind: 'wait' };
  }

  // Finished, failed, or paused runs must not keep a live tool/compaction
  // line. Compaction often starts without an end event when the run hits
  // its limit or completes mid-summarize, which left "Summarizing the work
  // so far" and "Saving a file" stuck under Ready for review.
  if (taskStatus && !isWorkActive(taskStatus)) {
    return null;
  }

  const list = Array.isArray(events) ? events : [];
  let lastTool = null;
  const activeTools = [];
  let lastToolEnded = false;
  let sawAgentStart = false;
  let sawResume = false;
  let sawSessionStart = false;
  let compacting = false;
  let justCompacted = false;
  let finalizing = null;
  let ended = false;

  for (const ev of list) {
    switch (ev?.type) {
      case 'session_finalizing':
        finalizing = ev.stage === 'artifacts' ? 'Saving files' : 'Finishing up';
        break;
      case 'session_end':
        finalizing = null;
        ended = true;
        break;
      case 'session_start':
        finalizing = null;
        ended = false;
        sawSessionStart = true;
        sawResume = false;
        sawAgentStart = false;
        activeTools.length = 0;
        lastTool = null;
        lastToolEnded = false;
        compacting = false;
        justCompacted = false;
        break;
      case 'session_resume':
        finalizing = null;
        ended = false;
        sawResume = true;
        sawAgentStart = false;
        activeTools.length = 0;
        lastTool = null;
        lastToolEnded = false;
        compacting = false;
        justCompacted = false;
        break;
      case 'agent_start':
        finalizing = null;
        ended = false;
        sawAgentStart = true;
        compacting = false;
        break;
      case 'tool_execution_start':
        if (isPlatformMediaFetch(ev)) break;
        activeTools.push(ev);
        lastTool = ev;
        lastToolEnded = false;
        compacting = false;
        justCompacted = false;
        break;
      case 'tool_execution_end':
        if (isPlatformMediaFetch(ev)) break;
        {
          const name = ev.toolName || ev.name;
          const index = !ev.toolCallId && !name ? activeTools.length - 1
            : activeTools.findLastIndex(start => ev.toolCallId
              ? start.toolCallId === ev.toolCallId
              : (start.toolName || start.name) === name);
          if (index >= 0) activeTools.splice(index, 1);
        }
        lastToolEnded = activeTools.length === 0;
        break;
      case 'compaction_start':
        activeTools.length = 0;
        compacting = true;
        lastToolEnded = true;
        break;
      case 'budget_pressure':
        if (ev.stage === 'compact') {
          activeTools.length = 0;
          compacting = true;
          lastToolEnded = true;
        }
        break;
      case 'compaction_end':
        compacting = false;
        justCompacted = ev.status === 'completed' && !ev.aborted;
        break;
      default:
        break;
    }
  }

  if (activeTools.length) {
    lastTool = activeTools[activeTools.length - 1];
    lastToolEnded = false;
  }
  if (ended) return null;
  if (finalizing) return { text: finalizing, icon: ACTIVITY_ICONS.wait, kind: 'info' };

  if (compacting) {
    return {
      text: 'Summarizing the work so far to make room',
      icon: 'compress',
      kind: 'info',
    };
  }

  if (justCompacted && !(lastTool && !lastToolEnded)) {
    const after = sanitizeWorkLiveStatus(
      llmStatus && !isGenericWorkStatus(llmStatus) ? String(llmStatus).trim() : '',
    );
    return {
      text: after || 'Picking up from the summary',
      icon: ACTIVITY_ICONS.think,
      kind: 'think',
    };
  }

  if (lastTool && !lastToolEnded) {
    const waitingOnDelegate = /^(task|task_status)$/i.test(String(lastTool.toolName || lastTool.name || ''));
    if (waitingOnDelegate) {
      const waiting = workSubagentWaitStatus(list);
      if (waiting) return waiting;
      return {
        text: 'Waiting for subagent',
        icon: ACTIVITY_ICONS.wait,
        kind: 'wait',
      };
    }
    const desc = describeWorkTool(lastTool);
    if (!desc?.hide && String(desc?.text || '').trim()) {
      return { text: desc.text, icon: desc.icon, kind: desc.kind, startedAt: Date.parse(lastTool.ts) || null };
    }
  }

  const waiting = workSubagentWaitStatus(list);
  if (waiting) return waiting;

  const llm = sanitizeWorkLiveStatus(isGenericWorkStatus(llmStatus) ? '' : String(llmStatus).trim());
  const booting = !sawAgentStart && (sawSessionStart || sawResume || isWorkActive(taskStatus));
  if (booting) {
    return {
      text: sawResume ? WORK_RESUME_STATUS : WORK_BOOT_STATUS,
      icon: ACTIVITY_ICONS.boot,
      kind: 'boot',
    };
  }
  if (sawAgentStart && !lastTool) {
    return { text: llm || WORK_PLANNING_STATUS, icon: ACTIVITY_ICONS.think, kind: 'think' };
  }
  if (sawAgentStart || lastToolEnded) {
    // Long SSE gaps after convert/export used to drop to generic thinking
    // while the next event was still in flight. Hold a wrap-up line from
    // the last tool so the transcript does not look idle.
    const wrap = lastTool && lastToolEnded && !llm
      ? workToolWrapStatus(lastTool)
      : (llm || WORK_THINKING_STATUS);
    return { text: wrap, icon: ACTIVITY_ICONS.think, kind: 'think' };
  }
  if (isWorkActive(taskStatus)) {
    return { text: llm || WORK_BOOT_STATUS, icon: ACTIVITY_ICONS.boot, kind: 'boot' };
  }
  return llm ? { text: llm, icon: ACTIVITY_ICONS.think, kind: 'think' } : null;
}

export function workActivityFromEvents(events, opts) {
  return workActivityMetaFromEvents(events, opts)?.text || null;
}

/**
 * Reduce an execution event to a compact activity line. Tool/turn/browser
 * events are live-only — they are never materialized into chat.messages, so
 * this is the only place they surface.
 */
function toolEventText(ev) {
  const content = ev?.result?.content || ev?.content || ev?.result;
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .filter((part) => part && (part.type === 'text' || typeof part.text === 'string'))
      .map((part) => part.text || '')
      .join('\n');
  }
  if (content && typeof content === 'object' && typeof content.text === 'string') return content.text;
  return '';
}

function modelFromToolEvent(ev) {
  const args = toolArgs(ev);
  if (args.model) return String(args.model).trim();
  const details = ev?.details || ev?.result?.details || {};
  if (details.model) return String(details.model).trim();
  if (ev?.result && typeof ev.result === 'object' && ev.result.model) {
    return String(ev.result.model).trim();
  }
  const match = toolEventText(ev).match(/(?:^|\n)Model:\s*([^\s\n]+)/i);
  return match ? match[1] : '';
}

/**
 * generate_image (and similar) often omit the model on the start event because
 * the platform default is filled in server-side. Copy the resolved slug from
 * the matching tool_execution_end onto the start args so the trail can name it.
 */
export function enrichWorkToolEvents(events) {
  const list = Array.isArray(events) ? events.map((ev) => {
    if (ev?.type !== 'tool_execution_start') return ev;
    return { ...ev, args: { ...toolArgs(ev) } };
  }) : [];
  const pending = [];
  const commands = new Map();
  for (const ev of list) {
    if (ev?.type === 'session_start' || ev?.type === 'session_resume') pending.length = 0;
    if (ev?.type === 'tool_execution_start') pending.push(ev);
    if (ev?.type !== 'tool_execution_end') continue;
    const command = workCommandFromEvent(ev);
    if (command?.jobId) commands.set(command.jobId, { ...commands.get(command.jobId), ...command });
    const model = modelFromToolEvent(ev);
    const progress = clipProgressFromToolEvent(ev);
    for (let i = pending.length - 1; i >= 0; i--) {
      const start = pending[i];
      if (String(start.toolName || '') !== String(ev.toolName || '')) continue;
      if (ev.toolCallId && start.toolCallId && ev.toolCallId !== start.toolCallId) continue;
      pending.splice(i, 1);
      if (command) start.command = { ...workCommandFromEvent(start), ...command };
      if (ev.isError || ev.result?.isError) start.isError = true;
      if (model && (!start.args.model || start.args.model !== model)) start.args.model = model;
      if (progress) {
        start.args.clip = progress.clip;
        if (progress.plannedClips) start.args.plannedClips = progress.plannedClips;
      }
      break;
    }
  }
  for (const event of list) if (event.command?.jobId && event.type === 'tool_execution_start') event.command = { ...event.command, ...commands.get(event.command.jobId) };
  return list;
}

// The orchestrator stamps each submitted clip with its place in the film
// (result.clip / result.plannedClips) so a 42-clip episode reads as progress
// instead of forty identical "Generating a video" lines.
function clipProgressFromToolEvent(ev) {
  const r = ev?.details || ev?.result?.details || ev?.result;
  if (!r || typeof r !== 'object') return null;
  const clip = Number(r.clip);
  if (!Number.isFinite(clip) || clip < 1) return null;
  const planned = Number(r.plannedClips);
  return { clip, plannedClips: Number.isFinite(planned) && planned >= clip ? planned : null };
}

export function clipProgressSuffix(args) {
  const clip = Number(args?.clip);
  if (!Number.isFinite(clip) || clip < 1) return '';
  const planned = Number(args?.plannedClips);
  return Number.isFinite(planned) && planned > 1 ? ` — clip ${clip} of ${planned}` : ` — clip ${clip}`;
}

function joinAnd(items) {
  if (items.length <= 1) return items[0] || '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function uncapitalize(text) {
  const value = String(text || '');
  if (!value) return value;
  return value.charAt(0).toLowerCase() + value.slice(1);
}

const NAMED_ACTOR_TOOLS = new Set(['run_model', 'list_models', 'run_agent', 'list_agents']);

// Specialist media tools do not always persist a model slug on later events
// (Stop / Resume is the usual case). Credit the provider that actually
// made the deliverable instead of falling back to the organizer.
const DEFAULT_MEDIA_TOOL_MODELS = {
  generate_image: 'gpt-image-2',
  edit_image: 'gpt-image-2',
  remove_background: 'ideogram',
  generate_3d: 'meshy',
  image_to_3d: 'meshy',
  threed_status: 'meshy',
  generate_sound_effect: 'cassetteai',
  generate_music: 'minimax-music',
};

function isWorkChatOrganizerSlug(slug) {
  const value = String(slug || '').toLowerCase();
  if (!value) return false;
  if (/meshy|hailuo|cassette|minimax|flux|kling|luma|runway|suno|udio|tripo|hunyuan|veo|nano-banana|gpt-image/.test(value)) {
    return false;
  }
  return /gemini|glm|claude|gpt-|gpt5|arya|grok|llama|qwen|deepseek|sonar|kimi/.test(value);
}

function workMediaModelSlug(toolName, argsModel) {
  const fallback = DEFAULT_MEDIA_TOOL_MODELS[String(toolName || '').toLowerCase()];
  const slug = String(argsModel || '').trim();
  if (slug && !isWorkChatOrganizerSlug(slug)) return slug;
  return fallback || slug;
}

/**
 * Settled (and live) one-line credit for a run: named models and agents,
 * then the tools. "Used Flux · generated an image"
 */
export function summarizeWorkAttribution(lines, { isLive = false, organizerModel = null } = {}) {
  const routedModel = [...(Array.isArray(lines) ? lines : [])].reverse().find((line) => line?.kind === 'model' && line.model)?.model;
  organizerModel = routedModel || organizerModel;
  const models = [];
  const seenModels = new Set();
  const agents = [];
  const seenAgents = new Set();

  const addModel = (slug) => {
    const name = friendlyWorkModelName(slug);
    if (!name) return;
    const key = name.toLowerCase();
    if (seenModels.has(key)) return;
    seenModels.add(key);
    models.push(name);
  };

  const namedSlugs = [];
  let sawMediaModel = false;

  for (const line of Array.isArray(lines) ? lines : []) {
    if (line?.kind !== 'tool') continue;
    if (line.model) {
      const toolName = String(line.name || '').toLowerCase();
      const slug = DEFAULT_MEDIA_TOOL_MODELS[toolName] && isWorkChatOrganizerSlug(line.model)
        ? DEFAULT_MEDIA_TOOL_MODELS[toolName]
        : line.model;
      if (DEFAULT_MEDIA_TOOL_MODELS[toolName] || !isWorkChatOrganizerSlug(slug)) {
        sawMediaModel = true;
      }
      namedSlugs.push(slug);
    }
    const agent = String(line.agent || '').trim();
    if (agent) {
      const key = agent.toLowerCase();
      if (!seenAgents.has(key)) {
        seenAgents.add(key);
        agents.push(agent);
      }
    }
  }

  if (!sawMediaModel) {
    for (const line of Array.isArray(lines) ? lines : []) {
      if (line?.kind !== 'tool') continue;
      const inferred = DEFAULT_MEDIA_TOOL_MODELS[String(line.name || '').toLowerCase()];
      if (inferred) {
        namedSlugs.push(inferred);
        sawMediaModel = true;
      }
    }
  }

  // A consult (run_model Arya) must not replace the organizer on a files
  // run. Media specialists still own the receipt — Flux made the image.
  // Organizer chat slugs (Arya / Gemini / GLM) consulted before generate_*
  // must not share that receipt ("Used Arya and GPT Image").
  // A settled Arya Q&A has no file/media tools — don't prepend Gemini.
  const hasOrganizerWork = (Array.isArray(lines) ? lines : []).some((line) => (
    line?.kind === 'tool' && !NAMED_ACTOR_TOOLS.has(String(line.name || '').toLowerCase())
  ));
  const namedArya = namedSlugs.some((slug) => String(slug || '').toLowerCase() === 'arya');
  const receiptSlugs = sawMediaModel
    ? namedSlugs.filter((slug) => !isWorkChatOrganizerSlug(slug))
    : namedSlugs;
  const orderedSlugs = [];
  if (!sawMediaModel && organizerModel && !(namedArya && !hasOrganizerWork)) {
    orderedSlugs.push(organizerModel);
  }
  orderedSlugs.push(...receiptSlugs);
  if (!orderedSlugs.length && organizerModel) orderedSlugs.push(organizerModel);
  for (const slug of orderedSlugs) addModel(slug);

  const toolsOnly = (Array.isArray(lines) ? lines : []).filter((line) => (
    line?.kind === 'tool' && !NAMED_ACTOR_TOOLS.has(String(line.name || '').toLowerCase())
  ));
  const toolSummary = summarizeWorkSteps(toolsOnly, { isLive });

  const parts = [];
  if (agents.length) {
    const who = joinAnd(agents);
    parts.push(isLive ? `Working with ${who}` : `Worked with ${who}`);
  }
  if (models.length) {
    const who = joinAnd(models);
    parts.push(isLive ? `Using ${who}` : `Used ${who}`);
  }
  if (toolSummary?.text) {
    parts.push(parts.length ? uncapitalize(toolSummary.text) : toolSummary.text);
  }

  if (!parts.length) return null;
  return {
    text: parts.join(' · '),
    icon: agents.length ? 'robot' : (models.length ? 'comments' : toolSummary?.icon || 'arrow-right'),
    models,
    agents,
  };
}

function activityLineSignature(line) {
  return `${line?.kind || ''}|${line?.name || ''}|${String(line?.text || '').replace(/\s+/g, ' ').trim()}`;
}

function workSubagentFallbackTitle(ev) {
  if (ev?.fanOut) return `${ev.fanOut} parallel items`;
  return ev?.role === 'coder' ? 'Coding worker' : ev?.role === 'worker' ? 'Worker' : 'Researcher';
}

function workSubagentLineFromEvent(ev) {
  const label = String(ev?.label || '').replace(/\s+/g, ' ').trim();
  const title = label ? truncate(label, 60) : workSubagentFallbackTitle(ev);
  return {
    kind: 'subagent',
    icon: 'sitemap',
    text: title,
    taskId: ev?.taskId ? String(ev.taskId) : '',
    label,
    role: ev?.role ? String(ev.role) : '',
    model: ev?.model ? String(ev.model) : '',
    status: ev?.status ? String(ev.status) : '',
    background: !!ev?.background,
    lastTool: ev?.lastTool ? String(ev.lastTool) : '',
    lastToolArgs: ev?.lastToolArgs && typeof ev.lastToolArgs === 'object' ? ev.lastToolArgs : null,
    toolCalls: Number(ev?.toolCalls) || 0,
    fanOut: Number(ev?.fanOut) || 0,
    okCount: ev?.okCount == null ? null : (Number(ev.okCount) || 0),
    partialCount: ev?.partialCount == null ? null : (Number(ev.partialCount) || 0),
  };
}

function workSubagentLineFromTaskTool(ev) {
  const args = toolArgs(ev);
  return workSubagentLineFromEvent({
    taskId: '',
    label: workDelegatedTaskLabel(args),
    role: String(args.agent || args.role || '').toLowerCase(),
    status: 'started',
    background: !!args.background,
  });
}

function mergeWorkSubagentLine(prev, next) {
  const label = next.label || prev.label;
  const terminal = ['finished', 'failed', 'stopped', 'interrupted'].includes(prev.status);
  if (terminal && ['started', 'progress'].includes(next.status)) return prev;
  return {
    ...prev,
    ...next,
    status: terminal && ['started', 'progress'].includes(next.status) ? prev.status : next.status,
    key: prev.key,
    taskId: next.taskId || prev.taskId,
    label,
    text: label ? truncate(label, 60) : (prev.text || next.text),
    model: next.model || prev.model,
    role: next.role || prev.role,
    lastTool: next.lastTool || prev.lastTool,
    lastToolArgs: next.lastToolArgs || prev.lastToolArgs,
    toolCalls: next.toolCalls || prev.toolCalls,
    fanOut: next.fanOut || prev.fanOut,
    okCount: next.okCount ?? prev.okCount,
    partialCount: next.partialCount ?? prev.partialCount,
  };
}

export function workRunningSubagentsFromEvents(events) {
  const byId = new Map();
  for (const ev of Array.isArray(events) ? events : []) {
    if (['session_start', 'session_resume'].includes(ev?.type)) byId.clear();
    if (ev?.type === 'user_message') {
      for (const [id, row] of byId) {
        if (!['started', 'progress'].includes(row.status)) byId.delete(id);
      }
    }
    if (ev?.type === 'session_end') {
      for (const [id, row] of byId) {
        if (['started', 'progress'].includes(row.status)) byId.set(id, { ...row, status: 'interrupted' });
      }
      continue;
    }
    if (ev?.type !== 'subagent') continue;
    const id = String(ev.taskId || '');
    if (!id) continue;
    const prev = byId.get(id) || {};
    if (['finished', 'failed', 'stopped', 'interrupted'].includes(prev.status) && ['started', 'progress'].includes(ev.status)) continue;
    byId.set(id, { ...prev, ...ev });
  }
  return [...byId.values()].filter((row) => row.status === 'started' || row.status === 'progress');
}

function workInFlightDelegateCount(events) {
  let n = 0;
  for (const ev of Array.isArray(events) ? events : []) {
    // Stop can close the runner without a matching tool end. Old tool
    // starts must not make the next turn wait for a cancelled delegate.
    if (['session_start', 'session_resume', 'session_end'].includes(ev?.type)) {
      n = 0;
      continue;
    }
    const name = String(ev?.toolName || ev?.name || '').toLowerCase();
    if (name !== 'task' && name !== 'task_status') continue;
    if (ev?.type === 'tool_execution_start') n += 1;
    else if (ev?.type === 'tool_execution_end' && n) n -= 1;
  }
  return n;
}

function workSubagentWaitStatus(events) {
  const running = workRunningSubagentsFromEvents(events).length;
  const inFlight = workInFlightDelegateCount(events);
  const n = Math.max(running, inFlight);
  if (!n) return null;
  return {
    text: n > 1 ? 'Waiting for subagents' : 'Waiting for subagent',
    icon: ACTIVITY_ICONS.wait,
    kind: 'wait',
  };
}

export function workSubagentModelLabel(line) {
  const model = friendlyWorkModelName(line?.model);
  if (model) return model;
  if (line?.role === 'coder') return 'Coding worker';
  if (line?.role === 'worker') return 'Worker';
  if (line?.role === 'explore') return 'Researcher';
  return '';
}

export function workSubagentActivityText(line) {
  if (!line) return '';
  if (line.status === 'failed') return 'Failed';
  if (line.status === 'stopped') return 'Stopped';
  if (line.status === 'interrupted') return 'Ended without a result';
  if (line.fanOut) {
    if (line.status === 'finished') {
      if (line.partialCount > 0) {
        const usable = Math.min(line.fanOut, Math.max(0, line.okCount ?? line.fanOut));
        const partial = Math.min(usable, line.partialCount);
        return `${usable - partial}/${line.fanOut} complete; ${partial} partial${usable < line.fanOut ? `; ${line.fanOut - usable} failed` : ''}`;
      }
      if (line.okCount != null && line.okCount < line.fanOut) {
        return `${line.okCount}/${line.fanOut} succeeded; ${line.fanOut - line.okCount} failed`;
      }
      return `Finished ${line.okCount ?? line.fanOut}/${line.fanOut} items`;
    }
    if (line.okCount) return `${line.okCount} of ${line.fanOut} items finished`;
    return `Working on ${line.fanOut} items in parallel`;
  }
  if (line.status === 'finished') return line.partialCount > 0 ? 'Partial result' : 'Finished';
  if (line.lastTool) {
    const desc = describeWorkTool({ toolName: line.lastTool, args: line.lastToolArgs || {} });
    const text = String(desc?.text || '').trim();
    if (text && !desc?.hide) return text;
  }
  if (line.role === 'coder') return 'Editing an isolated copy';
  if (line.role === 'worker') return 'Writing files';
  if (line.role === 'explore') return 'Investigating';
  return line.status === 'started' || line.status === 'progress' ? 'Working' : '';
}

/**
 * Activity trails keep tool/info lines only. Boot/thinking turns are status,
 * not history — they live in the run header and the live status row, where
 * they update in place. Putting them in the trail meant a "Thinking" line
 * appeared and then vanished when the next tool started. Consecutive
 * identical steps (two bash calls, two reads of the same file) collapse to
 * one row as they stream, so the trail never rewrites itself afterwards.
 * Subagent start/progress/finish for the same task_id fold into one nested
 * card so the current tool updates in place.
 */
export function filterWorkActivityLines(lines, { hideCurrentText } = {}) {
  const all = Array.isArray(lines) ? lines : [];
  const hasSubagent = all.some((line) => line?.kind === 'subagent');
  const collapsed = [];
  const subagentAt = new Map();
  const transfersAt = new Map();
  for (const line of all) {
    if (line?.transferId) {
      const index = transfersAt.get(line.transferId);
      if (index != null) { collapsed[index] = { ...line, key: collapsed[index].key }; continue; }
      transfersAt.set(line.transferId, collapsed.length);
    }
    if (line?.kind === 'turn' || line?.kind === 'divider' || line?.kind === 'end') continue;
    // Model routing is receipt metadata, not a step the user needs to follow.
    // Keep it in the run attribution without leading the activity trail with it.
    if (line?.kind === 'model') continue;
    if (hasSubagent && line?.kind === 'tool' && /^(task|task_status)$/i.test(String(line.name || ''))) continue;
    if (line?.kind === 'subagent') {
      const idKey = line.taskId ? `id:${line.taskId}` : '';
      const labelKey = String(line.label || '').replace(/\s+/g, ' ').trim().toLowerCase();
      const labelId = labelKey ? `label:${labelKey}` : '';
      let idx;
      if (idKey) idx = subagentAt.get(idKey);
      if (idx == null && labelId) {
        const candidate = subagentAt.get(labelId);
        // Labels are only a bridge from an optimistic launch card to its
        // lifecycle event. Two real workers may share a label but not an id.
        if (candidate != null && (!idKey || !collapsed[candidate]?.taskId)) idx = candidate;
      }
      if (idx != null) {
        collapsed[idx] = mergeWorkSubagentLine(collapsed[idx], line);
        if (idKey) subagentAt.set(idKey, idx);
        if (labelId) subagentAt.set(labelId, idx);
        continue;
      }
      const at = collapsed.length;
      if (idKey) subagentAt.set(idKey, at);
      if (labelId) subagentAt.set(labelId, at);
      collapsed.push(line);
      continue;
    }
    const prev = collapsed[collapsed.length - 1];
    if (prev && !line?.command && activityLineSignature(prev) === activityLineSignature(line)) continue;
    collapsed.push(line);
  }
  // The live status row already names the in-progress step. Repeating it
  // as the last trail line reads as the agent stuck saying the same thing.
  const needle = String(hideCurrentText || '').replace(/\s+/g, ' ').trim();
  if (needle) {
    const last = collapsed[collapsed.length - 1];
    if (last && last.kind !== 'subagent' && String(last.text || '').replace(/\s+/g, ' ').trim() === needle) {
      collapsed.pop();
    }
  }
  return collapsed;
}

export function workEventToLine(ev) {
  switch (ev?.type) {
    case 'project_transfer': {
      const event = slimWorkEvent(ev);
      if (!event.operationId) return null;
      const divisor = event.totalBytes >= 1024 * 1024 ? 1024 * 1024 : event.totalBytes >= 1024 ? 1024 : 1;
      const unit = divisor === 1 ? 'bytes' : divisor === 1024 ? 'KB' : 'MB';
      const amount = bytes => divisor === 1 ? String(bytes) : (bytes / divisor).toFixed(1);
      return { kind: 'progress', icon: 'file', transferId: event.operationId,
        text: `${event.operation === 'import' ? 'Importing from' : 'Saving to'} your Mac: ${amount(event.bytesTransferred)} of ${amount(event.totalBytes)} ${unit}` };
    }
    case 'model_routed':
      return ev.details?.model ? { kind: 'model', model: ev.details.model, text: `Using ${friendlyWorkModelName(ev.details.model)}` } : null;
    case 'session_start':
      return { kind: 'turn', icon: 'desktop', text: WORK_BOOT_STATUS };
    case 'session_resume':
      return { kind: 'turn', icon: 'desktop', text: WORK_RESUME_STATUS };
    case 'agent_start':
      return { kind: 'turn', icon: 'play', text: 'Getting to work' };
    case 'turn_start':
      return { kind: 'turn', icon: 'lightbulb', text: WORK_THINKING_STATUS };
    case 'message_end': {
      if (ev.message?.role !== 'assistant') return null;
      const content = Array.isArray(ev.message.content)
        ? ev.message.content
        : [{ type: 'text', text: String(ev.message.content || '') }];
      const text = content
        .filter((c) => c.type === 'text')
        .map((c) => c.text)
        .join(' ')
        .trim();
      return text ? { kind: 'message', text } : null;
    }
    case 'tool_execution_start': {
      if (isPlatformMediaFetch(ev)) return null;
      const toolName = String(ev.toolName || ev.name || '').toLowerCase();
      if (toolName === 'task') return workSubagentLineFromTaskTool(ev);
      if (toolName === 'task_status') return null;
      if (toolName === 'project_commands') {
        const args = toolArgs(ev);
        if (['status', 'output', 'cancel', 'list', 'listeners'].includes(args.operation)) return null;
        if (args.operation !== 'request_access') {
          const command = { ...workCommandFromEvent(ev), isError: !!ev.isError };
          return { kind: 'tool', name: toolName, text: command.jobId ? 'Ran a command' : 'Requested a command', icon: 'terminal', command };
        }
      }
      const desc = describeWorkTool(ev);
      if (desc?.hide || !String(desc?.text || '').trim()) return null;
      return {
        kind: 'tool',
        name: desc.name,
        text: desc.text,
        icon: desc.icon,
        model: desc.model || '',
        agent: desc.agent || '',
        ...(ev.isError ? { isError: true } : {}),
      };
    }
    case 'compaction_start':
      // A threshold probe can be cancelled without producing a summary.
      // Keep its live status, but don't append a misleading history entry
      // after the finished answer. Starts alone prove no completed work.
      return null;
    case 'compaction_end':
      return ev.status === 'completed' && !ev.aborted
        ? { kind: 'info', icon: 'compress', text: 'Summarized the work so far' }
        : null;
    case 'budget_rollover':
      // In-place rolls are billing bookkeeping on a live agent: a long film
      // rolls every minute or two, and a trail line each time reads as the
      // run stalling. Only a restart (fresh process + summary) is news.
      if (ev.inPlace) return null;
      return { kind: 'info', text: 'Summarized the work so far and kept going' };
    case 'budget_pressure':
      // Rollover already narrates this. A second "capacity" line makes a
      // healthy mid-run compact sound like the job ran out of budget.
      return null;
    case 'auto_retry_start':
    case 'auto_retry_end':
      // Pi retries a 402 / empty model turn before the host rolls the token
      // budget. Three of these fire in ~15s on a long film and read as the
      // job crashing. The next real tool line is the recovery.
      return null;
    case 'queue_update': {
      // Steering mid-run: the agent picks the message up at its next step, so
      // say so rather than leaving the user wondering if it was dropped. The
      // orchestrator steers too (budget wrap-up), and that is not the user's
      // message — it has its own line and must not be counted here.
      const steering = Array.isArray(ev.steering)
        ? ev.steering.filter((m) => !String(m?.message ?? m ?? '').startsWith('BUDGET NOTICE:'))
        : null;
      const queued = steering ? steering.length : Number(ev.queued) || 0;
      return queued > 0 ? { kind: 'info', text: 'Your message is queued for the next step' } : null;
    }
    case 'session_note':
      if (ev.kind === 'repository_progress') return { kind: 'progress', icon: 'comment', text: ev.note || '' };
      if (/context resume failed/i.test(ev.note || '')) {
        return { kind: 'info', text: 'Could not restore the previous context — starting fresh' };
      }
      if (INTERNAL_WORK_FAILURE.test(ev.note || '')) {
        return { kind: 'info', text: 'The work process stopped unexpectedly' };
      }
      if (INTERNAL_WORK_CONTINUE_NOTE.test(ev.note || '')) {
        if (/you sent a message instead of answering/i.test(ev.note || '')) {
          return { kind: 'info', text: 'You sent a message, so Work followed that instead of waiting' };
        }
        return null;
      }
      if (INTERNAL_WORK_ROOM_NOTE.test(ev.note || '')) return null;
      if (ev.hook === 'wedge' || INTERNAL_WORK_WEDGE_NOTE.test(ev.note || '')) return null;
      if (/summarized this run several times/i.test(ev.note || '')) {
        return {
          kind: 'info',
          text: 'This is a long job, so I’m working from a running summary. A new task would start fresher.',
        };
      }
      return { kind: 'info', text: ev.note || '' };
    case 'session_reaper':
      return { kind: 'info', text: 'Paused — no activity for a while' };
    case 'browser_login_requested':
      // The form itself takes over the composer; the timeline just records that
      // the run stopped here, and for which site.
      return {
        kind: 'info',
        text: ev.stage === 'code'
          ? 'Waiting for your verification code'
          : `Waiting for you to sign in${ev.url ? ` to ${workHostOf(ev.url)}` : ''}`,
      };
    case 'browser_login_complete':
      return { kind: 'info', text: 'Signed in — continuing' };
    case 'browser_login_skipped':
      return { kind: 'info', text: 'You skipped the sign-in' };
    case 'browser_login_timeout':
      return { kind: 'info', text: 'Sign-in timed out' };
    case 'browser_screenshot':
      return {
        kind: 'info',
        text: ev.url ? `Took a screenshot of ${workHostOf(ev.url)}` : 'Took a screenshot',
      };
    case 'browser_confirmation_requested':
      return {
        kind: 'info',
        text: ev.url
          ? `Asked for your approval on ${workHostOf(ev.url)}`
          : 'Asked for your approval before continuing',
      };
    case 'browser_confirmation_resolved':
      return {
        kind: 'info',
        text: ev.dismissed
          ? 'Left the approval unanswered and continued'
          : (ev.approved && ev.autoApproved
            ? 'Hands-off allowed the action — continuing'
            : ev.approved
            ? 'You approved the action — continuing'
            : 'You denied the action — Work adjusted'),
      };
    case 'user_question_requested':
      return {
        kind: 'info',
        text: ev.kind === 'credits'
          ? 'Asked you about credits before continuing'
          : (ev.kind === 'cost'
            ? 'Asked for your approval before spending credits'
            : (ev.kind === 'memory'
              ? 'Asked for your approval before changing memory'
              : (ev.kind === 'plan'
                ? 'Asked you to review the plan'
                : 'Asked you a question'))),
      };
    case 'plan_updated': {
      const p = workPlanProgress(ev.plan);
      if (!p.total) return null;
      if (ev.plan?.approved && p.done === 0) return { kind: 'info', icon: 'list-check', text: 'Plan approved — starting the work' };
      return {
        kind: 'info',
        icon: 'list-check',
        text: ev.plan?.revision > 1
          ? `Plan updated · ${p.done}/${p.total} steps done`
          : `Planned ${p.total} step${p.total === 1 ? '' : 's'}`,
      };
    }
    case 'subagent':
      return workSubagentLineFromEvent(ev);
    case 'checkpoint':
      if (ev.kind === 'restore') return { kind: 'info', icon: 'clock-rotate-left', text: ev.label || `Restored to turn ${ev.turn}` };
      return null;
    case 'skill_routed':
      return null;
    case 'site_published': {
      const site = slimPublishedSite(ev.site);
      return site ? { kind: 'info', icon: 'globe', text: `${ev.redeployed ? 'Updated' : 'Published'} ${site.name}.${SITES_DOMAIN}` } : null;
    }
    case 'site_unpublished': {
      const site = slimPublishedSite(ev.site);
      return site ? { kind: 'info', icon: 'globe', text: `Unpublished ${site.name}` } : null;
    }
    case 'user_question_resolved':
      return {
        kind: 'info',
        text: ev.skipped
          ? 'You skipped the question — Work picked a default'
          : (ev.dismissed
            ? 'Left the question unanswered and continued'
            : (ev.timedOut
              ? 'The question timed out'
              : (ev.answer ? `You answered: ${truncate(ev.answer, 72)}` : 'You answered — continuing'))),
      };
    case 'session_end':
      if (ev.status !== 'completed') {
        const reasonText = {
          coding_no_progress: 'Paused: repeated attempts made no coding progress',
          coding_plan_blocked: 'Paused: remaining coding steps are blocked',
          coding_model_unavailable: 'Paused: coding model unavailable after automatic retries',
          coding_recovery_failed: 'Paused: automatic coding recovery could not start',
          coding_worktrees_unresolved: 'Paused: coding changes still need integration',
          incomplete_deliverable: 'Paused: the requested result is still incomplete',
          idle_timeout: 'Paused: the worker stopped responding',
          goal_continuation_limit: 'Paused: goal continuation limit reached',
          budget_renewal_failed: 'Paused: the next run budget could not be renewed',
          budget_rollover_limit: 'Paused: automatic budget renewal limit reached',
          context_limit: 'Paused: task context could not be reduced enough',
        }[ev.reason];
        if (reasonText) return { kind: 'end', text: reasonText };
      }
      if (ev.status === 'completed') return { kind: 'divider', text: 'Ready for review' };
      if (ev.status === 'failed') return { kind: 'end', text: 'Paused after an unexpected problem' };
      if (ev.status === 'stopped') return { kind: 'end', text: 'Stopped' };
      if (ev.status === 'budget_exceeded') return { kind: 'end', text: 'Run limit reached' };
      if (ev.status === 'interrupted') return { kind: 'end', text: 'Paused' };
      return { kind: 'end', text: ev.status ? `Ended (${ev.status})` : 'Ended' };
    default:
      return null;
  }
}

// Steps collapse into one plain-language line per segment of a run ("Edited a
// file, worked in the workspace"), the way a colleague would summarize what they
// just did. Present tense while it is happening, past tense once it is done.
const COMMAND_REQUEST_GROUP = {
  id: 'mac-command-requests', icon: 'terminal', tools: [],
  live: (n) => `${n} command request${n === 1 ? '' : 's'}`,
  past: (n) => `${n} command request${n === 1 ? '' : 's'}`,
};
const STEP_GROUPS = [
  COMMAND_REQUEST_GROUP,
  { id: 'mac-commands', icon: 'terminal', tools: ['project_commands'], live: (n) => `running ${n} command${n === 1 ? '' : 's'}`, past: (n) => `ran ${n} command${n === 1 ? '' : 's'}` },
  { id: 'mac-folder', icon: 'folder-open', tools: ['project_files'], live: () => 'working in your Mac folder', past: () => 'worked in your Mac folder' },
  { id: 'calculation', icon: 'calculator', tools: ['calculate'], live: () => 'calculating', past: () => 'calculated the result' },
  { id: 'conversion', icon: 'arrow-right', tools: ['convert_units'], live: () => 'converting units', past: () => 'converted units' },
  {
    id: 'web',
    icon: 'globe',
    tools: ['web_research', 'web_search'],
    live: (n) => (n > 1 ? `searching the web · ${n} queries` : 'searching the web'),
    past: (n) => (n > 1 ? `ran ${n} web searches` : 'searched the web'),
  },
  {
    id: 'places',
    icon: 'location-dot',
    tools: ['places_search'],
    live: (n) => (n > 1 ? `looking up places · ${n} searches` : 'looking up places'),
    past: (n) => (n > 1 ? `looked up ${n} maps` : 'looked up places'),
  },
  {
    id: 'pages',
    icon: 'file-lines',
    tools: ['read_url', 'fetch_url', 'youtube_transcript'],
    live: (n) => (n > 1 ? `reading ${n} pages` : 'reading a page'),
    past: (n) => (n > 1 ? `read ${n} pages` : 'read a page'),
  },
  {
    id: 'browser',
    icon: 'window-maximize',
    tools: ['browser_navigate', 'browser_click', 'browser_type', 'browser_scroll', 'browser_extract', 'browser_snapshot', 'browser_screenshot', 'browser_ask_user_to_login', 'browser_request_confirmation'],
    live: () => 'using the browser',
    past: () => 'used the browser',
  },
  {
    id: 'edits',
    icon: 'pen',
    tools: ['write', 'edit'],
    live: (n) => (n > 1 ? `editing ${n} files` : 'editing a file'),
    past: (n) => (n > 1 ? `edited ${n} files` : 'edited a file'),
  },
  {
    id: 'files',
    icon: 'folder-open',
    tools: ['read', 'grep', 'glob'],
    live: () => 'looking through files',
    past: () => 'looked through files',
  },
  {
    id: 'commands',
    icon: 'desktop',
    tools: ['bash'],
    live: () => 'working in the workspace',
    past: () => 'worked in the workspace',
  },
  {
    id: 'skills',
    icon: 'book',
    tools: ['skill'],
    live: () => 'using a skill',
    past: () => 'used a skill',
  },
  {
    id: 'collaboration',
    icon: 'sitemap',
    tools: ['task'],
    live: () => 'delegating part of the work',
    past: () => 'delegated part of the work',
  },
  {
    id: 'planning',
    icon: 'list-check',
    tools: ['update_plan'],
    live: () => 'updating the plan',
    past: () => 'updated the plan',
  },
  {
    id: 'media',
    icon: 'image',
    tools: ['generate_image', 'edit_image', 'analyze_image', 'upscale_image', 'remove_background', 'vectorize_image', 'generate_video', 'image_to_video', 'extend_video', 'video_status', 'collect_video_jobs', 'generate_3d', 'image_to_3d', 'threed_status', 'generate_music', 'generate_sound_effect', 'music_status', 'text_to_speech'],
    live: () => 'generating media',
    past: () => 'generated media',
  },
  {
    id: 'documents',
    icon: 'file-import',
    tools: ['extract_document', 'convert_document', 'preview_document'],
    live: () => 'working through a document',
    past: () => 'worked through a document',
  },
  {
    id: 'audio',
    icon: 'file-audio',
    tools: ['transcribe_audio'],
    live: () => 'transcribing audio',
    past: () => 'transcribed audio',
  },
  {
    id: 'model',
    icon: 'comments',
    tools: ['run_model'],
    live: () => 'asking a model',
    past: () => 'asked a model',
  },
  {
    id: 'modelLookup',
    icon: 'search',
    tools: ['list_models'],
    live: () => 'looking up models',
    past: () => 'looked up models',
  },
  {
    id: 'credits',
    icon: 'calculator',
    tools: ['credit_balance', 'estimate_credits'],
    live: () => 'checking credits',
    past: () => 'checked credits',
  },
  {
    id: 'questions',
    icon: 'comments',
    tools: ['ask_user'],
    live: () => 'asking for your input',
    past: () => 'asked for your input',
  },
  {
    id: 'memory',
    icon: 'brain',
    tools: ['list_memories', 'manage_memory'],
    live: () => 'updating your memory',
    past: () => 'updated your memory',
  },
  {
    id: 'agents',
    icon: 'robot',
    tools: ['run_agent', 'list_agents'],
    live: () => 'working with an agent',
    past: () => 'worked with an agent',
  },
  {
    id: 'delivery',
    icon: 'file-export',
    tools: ['export_artifact', 'push_url'],
    live: (n) => (n > 1 ? `saving ${n} files` : 'saving a file'),
    past: (n) => (n > 1 ? `saved ${n} files` : 'saved a file'),
  },
];

const GROUP_BY_TOOL = new Map();
for (const group of STEP_GROUPS) {
  for (const tool of group.tools) GROUP_BY_TOOL.set(tool, group);
}

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

/**
 * One-line summary of a run segment's steps, plus the icon that best describes
 * it. Returns null when the segment did nothing worth naming.
 */
export function summarizeWorkSteps(lines, { isLive = false } = {}) {
  const counts = new Map();
  const toolsByGroup = new Map();
  let otherTool = null;
  let failedMedia = false;
  let failedDeliveries = 0;

  for (const line of Array.isArray(lines) ? lines : []) {
    if (line?.kind === 'subagent') {
      const group = GROUP_BY_TOOL.get('task');
      if (group) {
        counts.set(group.id, (counts.get(group.id) || 0) + 1);
        const names = toolsByGroup.get(group.id) || [];
        names.push('task');
        toolsByGroup.set(group.id, names);
      }
      continue;
    }
    if (line?.kind !== 'tool') continue;
    const name = String(line.name || '').toLowerCase();
    // A tool call can stop at approval or validation, before a native job is
    // created. Keep it in the history without counting it as a command run.
    const group = name === 'project_commands' && !line.command?.jobId
      ? COMMAND_REQUEST_GROUP : GROUP_BY_TOOL.get(name);
    if (group?.id === 'delivery' && line.isError) {
      failedDeliveries += 1;
      continue;
    }
    if (group?.id === 'media' && line.isError) {
      failedMedia = true;
      continue;
    }
    if (!group) {
      if (!otherTool) otherTool = line.text;
      continue;
    }
    counts.set(group.id, (counts.get(group.id) || 0) + 1);
    const names = toolsByGroup.get(group.id) || [];
    names.push(name);
    toolsByGroup.set(group.id, names);
  }

  const deliveryFailure = failedDeliveries === 1 ? 'file export failed' : `${failedDeliveries} file exports failed`;
  if (counts.size === 0 && failedDeliveries) return { icon: 'file-export', text: capitalize(deliveryFailure) };
  if (counts.size === 0) return failedMedia
    ? { icon: 'image', text: 'Media attempt failed' }
    : otherTool ? { icon: 'arrow-right', text: otherTool } : null;

  const ordered = STEP_GROUPS.filter((group) => counts.has(group.id));
  const phrases = ordered.map((group) => {
    if (group.id === 'media') return mediaPhrase(toolsByGroup.get(group.id) || [], isLive);
    return isLive ? group.live(counts.get(group.id)) : group.past(counts.get(group.id));
  });
  if (failedDeliveries) phrases.push(deliveryFailure);

  const first = ordered[0];
  const icon = first.id === 'media' ? mediaIcon(toolsByGroup.get('media') || []) : first.icon;
  return { icon, text: capitalize(phrases.join(', ')) };
}

function countNamedTools(names, tools) {
  let n = 0;
  for (const name of names) if (tools.has(name)) n += 1;
  return n;
}

function mediaNoun(kind, count) {
  const n = Math.max(1, count);
  if (kind === 'image') return n === 1 ? 'an image' : `${n} images`;
  if (kind === 'video') return n === 1 ? 'a video' : `${n} videos`;
  if (kind === '3d') return n === 1 ? 'a 3D model' : `${n} 3D models`;
  if (kind === 'song') return n === 1 ? 'a song' : `${n} songs`;
  if (kind === 'sfx') return n === 1 ? 'a sound effect' : `${n} sound effects`;
  return n === 1 ? 'spoken audio' : `${n} spoken clips`;
}

function mediaKinds(names) {
  const set = new Set(names);
  const analyze = set.has('analyze_image');
  const image = set.has('generate_image') || set.has('edit_image')
    || set.has('upscale_image') || set.has('remove_background') || set.has('vectorize_image');
  const video = set.has('generate_video') || set.has('image_to_video') || set.has('extend_video') || set.has('video_status') || set.has('collect_video_jobs');
  const mesh = set.has('generate_3d') || set.has('image_to_3d') || set.has('threed_status');
  const sfx = set.has('generate_sound_effect');
  const song = set.has('generate_music') || (set.has('music_status') && !sfx);
  const speech = set.has('text_to_speech');
  return [analyze && 'vision', image && 'image', video && 'video', mesh && '3d', song && 'song', sfx && 'sfx', speech && 'speech'].filter(Boolean);
}

function mediaIcon(names) {
  const kinds = mediaKinds(names).filter((kind) => kind !== 'vision');
  if (kinds.length === 1 && kinds[0] === '3d') return 'cube';
  if (kinds.length === 1 && (kinds[0] === 'song' || kinds[0] === 'sfx' || kinds[0] === 'speech')) return 'music';
  return 'image';
}

function mediaPhrase(names, isLive) {
  const kinds = mediaKinds(names);
  if (kinds.length === 1) {
    if (kinds[0] === 'vision') return isLive ? 'looking at an image' : 'looked at an image';
    const starts = kinds[0] === 'image' ? countNamedTools(names, new Set(['generate_image', 'edit_image', 'upscale_image', 'remove_background', 'vectorize_image']))
      : kinds[0] === 'video' ? countNamedTools(names, new Set(['generate_video', 'image_to_video', 'extend_video']))
      : kinds[0] === '3d' ? countNamedTools(names, new Set(['generate_3d', 'image_to_3d']))
      : kinds[0] === 'song' ? countNamedTools(names, new Set(['generate_music']))
        : kinds[0] === 'sfx' ? countNamedTools(names, new Set(['generate_sound_effect']))
          : countNamedTools(names, new Set(['text_to_speech']));
    // Async video tools submit jobs; a finished tool call or status poll
    // does not prove a video was generated or delivered.
    if (kinds[0] === 'video') {
      if (!starts) return isLive ? 'checking video progress' : 'checked video progress';
      const clips = starts === 1 ? 'a video clip' : `${starts} video clips`;
      return isLive ? `generating ${clips}` : `requested ${clips}`;
    }
    const noun = mediaNoun(kinds[0], starts);
    return isLive ? `generating ${noun}` : `generated ${noun}`;
  }
  return isLive ? 'generating media' : kinds.includes('video') ? 'worked on media' : 'generated media';
}

export function formatWorkDuration(ms) {
  if (!Number.isFinite(ms) || ms < 1000) return null;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const rest = seconds % 60;
    return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes ? `${hours}h ${restMinutes}m` : `${hours}h`;
}

/**
 * Billed credit cost for a settled Work receipt. Tokens stay internal
 * (run budget); credits are what the user is charged.
 */
export function formatWorkCostNote({ creditsUsed = null, scope = 'run' } = {}) {
  if (creditsUsed == null || !Number.isFinite(Number(creditsUsed)) || Number(creditsUsed) < 0) return null;
  const credits = Math.round(Number(creditsUsed));
  return `${credits.toLocaleString()} ${credits === 1 ? 'credit' : 'credits'}${scope === 'task' ? ' task total' : ''}`;
}

/** Credits billed through a slimmed tool event (result bodies are stripped). */
export function creditsFromWorkEvent(ev) {
  const n = Number(
    ev?.creditsUsed
    ?? ev?.details?.creditsUsed
    ?? ev?.result?.creditsUsed
    ?? ev?.result?.usage?.credits_used
    ?? ev?.result?.details?.creditsUsed
  );
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
}

export function sumWorkEventCredits(events) {
  let total = 0;
  for (const ev of Array.isArray(events) ? events : []) {
    if (ev?.type && ev.type !== 'tool_execution_end') continue;
    total += creditsFromWorkEvent(ev);
  }
  return total;
}

/**
 * Plain-language capacity for the Work rail. The execution budget is measured
 * in tokens internally, but that unit does not help someone decide whether a
 * run has room to keep going. Keep the exact percentage in the accessible
 * label while the visible copy answers that product question directly.
 */
export function workCapacityMeta(spent, total) {
  const limit = Number(total);
  if (!Number.isFinite(limit) || limit <= 0) return null;

  const used = Math.max(0, Number(spent) || 0);
  const fraction = used / limit;
  const usedPercent = Math.min(100, Math.max(0, Math.round(fraction * 100)));
  let label = 'Full capacity available';
  if (fraction >= 1) label = 'Run limit reached';
  else if (fraction >= 0.9) label = 'Nearly at the run limit';
  else if (fraction >= 0.7) label = 'Approaching the run limit';
  else if (fraction >= 0.45) label = 'Room to keep going';
  else if (fraction > 0) label = 'Plenty of room remaining';

  return {
    label,
    fraction,
    usedPercent,
    accessibilityLabel: `${usedPercent}% of run capacity used`,
  };
}

const MS = (ts) => {
  const value = ts ? new Date(ts).getTime() : NaN;
  return Number.isFinite(value) ? value : null;
};

// Longest gap we are willing to attribute to a single turn when we have to
// fall back to message timestamps (no event log for older sessions).
const MAX_FALLBACK_TURN_MS = 6 * 60 * 60 * 1000;

const matchKey = (text) => String(text || '').replace(/\s+/g, ' ').trim().slice(0, 60).toLowerCase();

// A reply that shipped something — a file, an image, an artifact — is the run's
// output, not narration, so it never gets folded away.
const carriesDeliverable = (message) => Boolean(
  message?.files?.length
  || message?.images?.length
  || message?.parameters?.coworkerArtifacts?.length,
);

/**
 * Structure-only key for Work run projection. Streaming tokens change
 * content, not ids / roles / empty-draft-ness, so this stays stable across
 * ~20 store flushes a second and lets ChatView skip rebuildWorkRuns.
 */
export function workMessageProjectionKey(messages) {
  const list = Array.isArray(messages) ? messages : [];
  let key = '';
  for (let i = 0; i < list.length; i++) {
    const message = list[i];
    key += `${message?.id || ''}:${message?.role || ''}:${isWorkEmptyLiveDraft(message) ? '1' : '0'}:${MS(message?.timestamp || message?.date || message?.createdAt) || 0}|`;
  }
  return key;
}

/**
 * Pi emits message_start (and we paint isTyping/isStreaming) before any
 * tokens exist. That empty draft is not a reply — treating it as one stacks
 * the streaming cursor, a timestamp, and the Work live spinner. The same is
 * true after a token-less turn closes: a blank Gab AI stamp is not an answer.
 */
export function isWorkEmptyLiveDraft(message) {
  if (message?.role !== 'assistant') return false;
  if (String(message?.content || '').trim()) return false;
  if (carriesDeliverable(message)) return false;
  // Thinking-only drafts are not replies. Work already shows thinking as the
  // live status line; mounting a thought panel then tearing it down jitters
  // the transcript.
  return true;
}

const WORK_ANSWER_MIN_CHARS = 160;

const CLIP_PROMPT_RE = /\bclip\s+\d+\b/i;
// Line-start headings, including markdown (`**Clip 1**`, `### Clip 1`).
// Mid-sentence "extending clip 12" must not count as a breakdown.
const CLIP_HEADING_RE = /(?:^|\n)\s*(?:#{1,6}\s+)?(?:\*{1,3}|_{1,3})?\s*clip\s+\d+\b/gi;
const SCRIPT_APPROVAL_RE = /\b(?:approve(?:\s+the)?\s+script|script breakdown|start generating|revise the script|clip prompts|script)\b/i;

function workTextLooksStructured(value) {
  if (/^#{1,6}\s/m.test(value)) return true;
  if ((value.match(/\n\n/g) || []).length >= 2) return true;
  if ((value.match(/\n/g) || []).length >= 3) return true;
  if (CLIP_PROMPT_RE.test(value)) return true;
  if (/```/.test(value)) return true;
  if ((value.match(/^\s*(?:[-*]|\d+[.)])\s/gm) || []).length >= 2) return true;
  return false;
}

export function workTextHasClipPrompts(text) {
  const headings = String(text || '').match(CLIP_HEADING_RE);
  return (headings?.length || 0) >= 2;
}

export function isWorkScriptApprovalQuestion(question) {
  const labels = [question?.question];
  for (const option of question?.options || []) labels.push(option?.label);
  for (const item of question?.questions || []) {
    labels.push(item?.question);
    for (const option of item?.options || []) labels.push(option?.label);
  }
  return SCRIPT_APPROVAL_RE.test(labels.filter(Boolean).join(' '));
}

export const WORK_SCRIPT_APPROVAL_PROMPT = 'Does this script look good?';

const CLIP_APPROVAL_RE = /\b(?:approve(?:\s+the)?\s+clips?|stitch(?:\s+the)?\s+(?:film|video)|redo a clip|generated clips)\b/i;

export function isWorkClipApprovalQuestion(question) {
  const labels = [question?.question];
  for (const option of question?.options || []) labels.push(option?.label);
  for (const item of question?.questions || []) {
    labels.push(item?.question);
    for (const option of item?.options || []) labels.push(option?.label);
  }
  return CLIP_APPROVAL_RE.test(labels.filter(Boolean).join(' '));
}

export const WORK_CLIP_APPROVAL_PROMPT = 'Do you approve these clips?';

export function workClipApprovalCardText(text) {
  const value = String(text || '').trim();
  if (!value) return WORK_CLIP_APPROVAL_PROMPT;
  if (CLIP_APPROVAL_RE.test(value) || value.length > 240) return WORK_CLIP_APPROVAL_PROMPT;
  return workQuestionDisplayText(value);
}

const STILLS_APPROVAL_RE = /\b(?:approve(?:\s+the)?\s+stills?|redo a (?:character|keyframe|still)|character sheet|keyframes?)\b/i;
const VISUAL_PROMPT_APPROVAL_RE = /\bapprove(?:\s+(?:the|these|saved|\d+))*\s+(?:visual|shot|keyframe)\s+(?:prompts?|plan)\b/i;

export function isWorkStillsApprovalQuestion(question) {
  const labels = [question?.question];
  for (const option of question?.options || []) labels.push(option?.label);
  for (const item of question?.questions || []) {
    labels.push(item?.question);
    for (const option of item?.options || []) labels.push(option?.label);
  }
  const text = labels.filter(Boolean).join(' ');
  // Approving prompts to create future keyframes is not reviewing images.
  if (VISUAL_PROMPT_APPROVAL_RE.test(text) && !/\b(?:approve(?:\s+the)?\s+stills?|redo a (?:character|keyframe|still))\b/i.test(text)) return false;
  return STILLS_APPROVAL_RE.test(text);
}

export const WORK_STILLS_APPROVAL_PROMPT = 'Do these stills look consistent?';

export function workStillsApprovalCardText(text) {
  const value = String(text || '').trim();
  if (!value) return WORK_STILLS_APPROVAL_PROMPT;
  if (STILLS_APPROVAL_RE.test(value) || value.length > 240) return WORK_STILLS_APPROVAL_PROMPT;
  return workQuestionDisplayText(value);
}

/** Question-box copy only — clip prompts belong in the streamed message. */
export function workScriptApprovalCardText(text) {
  const value = String(text || '').trim();
  if (!value) return WORK_SCRIPT_APPROVAL_PROMPT;
  if (workTextHasClipPrompts(value)) return WORK_SCRIPT_APPROVAL_PROMPT;
  const headings = value.match(CLIP_HEADING_RE) || [];
  if (headings.length >= 1 && value.length > 240) return WORK_SCRIPT_APPROVAL_PROMPT;
  return workQuestionDisplayText(value);
}

/** Latest assistant clip-prompt breakdown, for the script-approval card. */
export function latestWorkScriptPreview(messages, { maxChars = WORK_SCRIPT_PREVIEW_MAX_CHARS, question } = {}) {
  const attached = String(question?.scriptPreview || '').trim();
  if (attached && CLIP_PROMPT_RE.test(attached)) {
    if (attached.length <= maxChars) return attached;
    return `${attached.slice(0, maxChars).trimEnd()}\n…`;
  }
  const rows = Array.isArray(messages) ? messages : [];
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i];
    if (row?.role !== 'assistant') continue;
    const text = workMessageForDisplay(row.content).trim();
    if (!workTextHasClipPrompts(text)) continue;
    if (text.length <= maxChars) return text;
    return `${text.slice(0, maxChars).trimEnd()}\n…`;
  }
  return '';
}

/** Long / structured Work text is the answer, not a one-line status note. */
export function looksLikeWorkAnswer(text) {
  const value = String(text || '').trim();
  // A mid-run "let me fix the script…" note can exceed 160 chars and used
  // to render as the customer's answer (Gab AI Work Mode Overview).
  if (WORK_PROGRESS_NARRATION.test(value) && value.length <= 400 && !workTextLooksStructured(value)) {
    return false;
  }
  if (value.length >= WORK_ANSWER_MIN_CHARS) return true;
  return workTextLooksStructured(value);
}

/** Workspace bookkeeping the customer should never see as a reply. */
export function isWorkInternalBookkeeping(text) {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  if (!value || value.length > 280 || workTextReportsBlocker(value)) return false;
  return WORK_INTERNAL_NOTE.test(value);
}

/** Intent-to-act / status lines that duplicate the live trail — not something to review. */
export function isWorkFillerNarration(text) {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  if (!value) return true;
  if (isWorkInternalBookkeeping(value)) return true;
  if (looksLikeWorkAnswer(text)) return false;
  if (GENERIC_STATUS.test(value)) return true;
  if (value.length <= 220 && WORK_PROGRESS_NARRATION.test(value)) return true;
  return false;
}

/**
 * Live plain-text narration belongs in the slim progress style, not the
 * answer bubble. Interactive, error, and deliverable-bearing rows stay in
 * the full message renderer. Streaming tokens and long answers use the same
 * chat bubble as normal mode so the essay grows in place instead of dumping
 * at settle. Short completed comments while the run is still working stay
 * as progress notes so they do not collapse when the next reply arrives.
 */
export function isWorkProgressMessage(message, runMeta) {
  if (message?.role !== 'assistant' || !runMeta) return false;
  // A turn can "end" on a let-me-fix note while the run keeps going. Treat
  // that as progress, not a reviewable answer, even after the turn closes.
  if (runMeta.isFinalReply && !runMeta.isLiveStep && !isWorkFillerNarration(message.content)) {
    return false;
  }
  if (message.isStreaming || message.isTyping) return false;
  if (looksLikeWorkAnswer(message.content)) return false;
  if (!String(message?.content || '').trim()) return false;
  // A draft, script, or other reviewable reply is not "working" chatter
  // just because it is shorter than a finished essay.
  if (!isWorkFillerNarration(message.content) && String(message.content).trim().length >= 120) {
    return false;
  }
  if (message?.isError || message?.hasError) return false;
  if (carriesDeliverable(message)) return false;
  if (message?.buttons?.length
    || message?.products?.length
    || message?.stockQuotes?.length
    || message?.places?.length
    || message?.map
    || message?.inferences?.length
    || message?.isRequestingMedia) return false;
  return true;
}

// Two or more short comments in a row is enough yap to fold. A single note
// stays — hiding every in-progress reply made the transcript look stalled.
export const WORK_PROGRESS_FOLD_FROM = 2;

/**
 * Fold consecutive progress notes so the transcript keeps the work (tools,
 * live status, the answer) without a stack of "let me…" comments. While the
 * run is live, the latest note stays visible so something is still happening
 * on screen; earlier notes collapse into one row that expands in place.
 */
export function annotateWorkProgressFolds(runs, byMessageId, {
  isActive = false,
  pendingUserGate = false,
} = {}) {
  if (!Array.isArray(runs) || !byMessageId) return byMessageId;

  runs.forEach((run, runIndex) => {
    const isLive = !!isActive && runIndex === runs.length - 1;
    const replies = run?.replies || [];
    let i = 0;
    while (i < replies.length) {
      const message = replies[i];
      const meta = byMessageId[message?.id] || {};
      if (!message?.id || !isFoldableWorkProgress(message, meta, { pendingUserGate: isLive && pendingUserGate })) {
        i += 1;
        continue;
      }
      let j = i + 1;
      while (j < replies.length) {
        const next = replies[j];
        if (!next?.id || !isFoldableWorkProgress(
          next,
          byMessageId[next.id] || {},
          { pendingUserGate: isLive && pendingUserGate },
        )) break;
        j += 1;
      }
      applyWorkProgressFoldToGroup(replies.slice(i, j), byMessageId, { isLive });
      i = j;
    }
  });
  return byMessageId;
}

function isFoldableWorkProgress(message, runMeta, { pendingUserGate = false } = {}) {
  if (!isWorkProgressMessage(message, runMeta)) return false;
  // An ask_user card is not a substitute for the draft it is asking about.
  if (pendingUserGate && !isWorkFillerNarration(message.content)) return false;
  return true;
}

function applyWorkProgressFoldToGroup(group, byMessageId, { isLive }) {
  if (!Array.isArray(group) || group.length < WORK_PROGRESS_FOLD_FROM) return;

  const last = group[group.length - 1];
  const lastMeta = byMessageId[last.id] || {};
  // Only the comment still being worked toward stays in the open — a long
  // answer further down is the thing to watch, so earlier notes all fold.
  const keepLast = isLive && !!lastMeta.isLiveStep;
  const hidden = keepLast ? group.slice(0, -1) : group;
  if (!hidden.length) return;

  const key = hidden[0].id;
  hidden.forEach((message, index) => {
    byMessageId[message.id] = {
      ...(byMessageId[message.id] || {}),
      progressFold: {
        key,
        role: index === 0 ? 'lead' : 'member',
        hiddenCount: hidden.length,
        keepLatest: keepLast,
      },
    };
  });
  if (keepLast) {
    byMessageId[last.id] = {
      ...(byMessageId[last.id] || {}),
      progressKeep: true,
    };
  }
}

export function workProgressFoldLabel({ hiddenCount = 0, keepLatest = false } = {}) {
  const count = Math.max(0, Number(hiddenCount) || 0);
  const noun = count === 1 ? 'note' : 'notes';
  if (keepLatest) return `${count} earlier ${noun}`;
  return `${count} working ${noun}`;
}

/** Progress-note bodies hide until the in-transcript fold is opened. */
export function shouldShowWorkProgressBody(workRun, { foldOpen = false } = {}) {
  if (!workRun?.progressFold) return true;
  return !!foldOpen;
}

/**
 * Copy/share/more belong on the settled answer of a run. A live latest
 * reply is still in-between work, even though it is last in the run so far.
 */
export function isWorkCompletedFinalReply(workRun) {
  return !!workRun?.isFinalReply && !workRun.isLive && !workRun.isLiveStep;
}

/**
 * Split the execution event log into agent turns. A turn is every step taken
 * between one assistant message and the next, rendered inline under that reply.
 */
export function buildWorkTurns(events) {
  const turns = [];
  let lines = [];
  let startTs = null;
  // A worker can span several assistant replies. Keep its lifecycle on the
  // original card instead of leaving one permanently-running card per reply.
  const workers = new Map();

  for (const ev of enrichWorkToolEvents(events)) {
    if (['session_start', 'session_resume'].includes(ev?.type)) {
      for (const worker of workers.values()) {
        if (['started', 'progress'].includes(worker.status)) worker.status = 'interrupted';
      }
      workers.clear();
    }
    // A steer is a user message too. Keep active workers attached to their
    // original cards while allowing a later request to reuse a finished ID.
    if (ev?.type === 'user_message') {
      for (const [id, worker] of workers) {
        if (!['started', 'progress'].includes(worker.status)) workers.delete(id);
      }
    }
    if (ev?.type === 'session_end') {
      for (const worker of workers.values()) {
        if (['started', 'progress'].includes(worker.status)) {
          worker.status = ev.status === 'stopped' ? 'stopped' : 'interrupted';
        }
      }
    }
    const ts = MS(ev?.ts);
    // A new or resumed session is a new clock. Leftover events after the last
    // reply (session_end, idle reaper) must not make the next turn look like
    // it has already been running for minutes.
    if (ev?.type === 'session_start' || ev?.type === 'session_resume') {
      // A silent turn's tools + session_end sit in `lines` until an
      // assistant message closes them. Resume used to drop that buffer, so
      // an empty first request lost its trail and receipt after follow-up.
      if (lines.length) {
        turns.push({ startTs, endTs: ts, text: '', lines, seq: Number(ev.seq) });
      }
      lines = [];
      startTs = ts;
    } else if (startTs === null && ts !== null) {
      startTs = ts;
    }
    const line = workEventToLine(ev);

    if (ev?.type === 'message_end' && ev.message?.role === 'assistant') {
      const text = line?.kind === 'message' ? line.text : '';
      if (text) {
        turns.push({ startTs, endTs: ts, text, lines, seq: Number(ev.seq) });
        lines = [];
        startTs = null;
      }
      continue;
    }
    if (line && line.kind !== 'message') {
      const row = { ...line, key: ev.seq, ts: ev.ts };
      if (row.kind === 'subagent' && row.taskId) {
        const previous = workers.get(row.taskId);
        if (previous) {
          Object.assign(previous, mergeWorkSubagentLine(previous, row));
          continue;
        }
        workers.set(row.taskId, row);
      }
      lines.push(row);
    }
  }

  const live = (lines.length > 0 || startTs !== null) ? { startTs, lines } : null;
  return { turns, live };
}

/**
 * Match each assistant message to the event-log turn that produced it.
 * Official Work ids encode the message_end seq (`cw-<task>-<seq>`); that
 * pairing stays unique even when several replies share the same text.
 * Text matching is the fallback for older rows (UUID ids, aged-out events).
 */
function matchTurnsToMessages(messages, turns) {
  const indexByMessageId = {};
  const unmatched = turns.map((turn, index) => ({
    key: matchKey(turn.text),
    seq: Number(turn.seq),
    index,
  }));

  for (const message of messages) {
    if (message?.role !== 'assistant' || !message.id) continue;
    const seq = workSeqFromMessageId(message.id);
    if (seq) {
      const at = unmatched.findIndex((row) => row.seq === seq);
      if (at !== -1) {
        indexByMessageId[message.id] = unmatched[at].index;
        unmatched.splice(at, 1);
        continue;
      }
    }
    const key = matchKey(message.content);
    if (!key) continue;
    const at = unmatched.findIndex(({ key: turnKey }) => (
      turnKey && (turnKey === key || turnKey.startsWith(key) || key.startsWith(turnKey))
    ));
    if (at === -1) continue;
    indexByMessageId[message.id] = unmatched[at].index;
    unmatched.splice(at, 1);
  }

  return indexByMessageId;
}

/** True when the latest user turn exported a file the customer can open. */
export function workTurnHasDeliverable(events) {
  return workRunHasDeliverable(events, null);
}

/** Artifacts exported after `runStartTs` and before the next user message. */
export function workRunHasDeliverable(events, runStartTs) {
  const start = MS(runStartTs);
  let inRun = start === null;
  let delivered = false;
  for (const ev of Array.isArray(events) ? events : []) {
    if (!ev) continue;
    if (ev.type === 'user_message') {
      const ts = MS(ev.ts);
      if (start !== null) {
        if (!inRun) {
          if (ts === null || ts >= start) {
            inRun = true;
            delivered = false;
          }
          continue;
        }
        break;
      }
      delivered = false;
      continue;
    }
    if (inRun && ev.type === 'artifact_ready') delivered = true;
  }
  return delivered;
}

// Exclude observed pauses within this request, preserving the clock for new follow-ups.
function workRunPausedMs(events, startTs, endTs) {
  if (startTs === null || endTs === null) return 0;
  let pausedAt = null;
  let pausedMs = 0;
  for (const event of Array.isArray(events) ? events : []) {
    const ts = MS(event?.ts);
    if (ts === null) continue;
    if (event.type === 'session_end') {
      if (pausedAt === null && ts >= startTs) pausedAt = ts;
    } else if (event.type === 'session_resume' || event.type === 'session_start') {
      if (pausedAt !== null) {
        pausedMs += Math.max(0, Math.min(ts, endTs) - Math.max(pausedAt, startTs));
      }
      pausedAt = null;
    }
  }
  return pausedMs;
}

/**
 * Group a Work transcript into runs — one user request and everything the agent
 * did in response — and place every activity line at its true chronological
 * position: each step attaches to the transcript row (user or assistant) it
 * followed, ordered by event seq. This is what makes the transcript an
 * append-only timeline. Steps taken before the first reply live under the
 * request that asked for them (and stay there when the reply lands), a
 * steering message slots between the steps that preceded it and the steps
 * that answered it, and nothing re-homes after being on screen. The run's
 * settled account (time + attribution) closes the run under its final reply.
 */
export function buildWorkRuns(messages, events, {
  isActive = false,
  organizerModel = null,
  turns: precomputed = null,
  creditReceipts = [],
} = {}) {
  const list = Array.isArray(messages) ? messages : [];
  const { turns, live } = (precomputed && Array.isArray(precomputed.turns))
    ? precomputed
    : buildWorkTurns(events);
  const turnIndexByMessageId = matchTurnsToMessages(list, turns);

  // A user message's place in the timeline is the moment the user SENT it,
  // not the moment the agent got around to reading it. A steer shows up in
  // the log twice: in a queue_update the instant it is sent, and as a
  // user_message when it is delivered (possibly several tool calls later —
  // the synced row's id encodes THAT seq). Anchoring the bubble to the
  // earliest mention keeps it above the steps that ran after the user typed,
  // and gives optimistic msg_* bubbles their position before the sync
  // renames them.
  const userEventPool = [];
  for (const ev of Array.isArray(events) ? events : []) {
    if (ev?.type === 'user_message') {
      userEventPool.push({ seq: Number(ev.seq), key: matchKey(stripWorkInjectedPrompt(ev.text)), used: false });
    } else if (ev?.type === 'queue_update' && Array.isArray(ev.steering)) {
      for (const entry of ev.steering) {
        const key = matchKey(stripWorkInjectedPrompt(entry?.message ?? entry));
        if (key) userEventPool.push({ seq: Number(ev.seq), key, used: false });
      }
    }
  }
  const claimUserEventSeq = (content) => {
    const key = matchKey(stripWorkInjectedPrompt(content));
    if (!key) return null;
    const entry = userEventPool.find((row) => !row.used && row.key === key);
    if (!entry || !Number.isFinite(entry.seq)) return null;
    entry.used = true;
    return entry.seq;
  };

  const runs = [];
  const boundaries = [];
  const hiddenMessageIds = new Set();
  const resumedAtSeqById = new Map();
  for (const message of list) {
    if (!message?.id) continue;
    const ts = MS(message.timestamp || message.date || message.createdAt);
    if (message.role === 'user') {
      const idSeq = workSeqFromMessageId(message.id);
      const sentSeq = claimUserEventSeq(message.content);
      const seq = (idSeq !== null && sentSeq !== null) ? Math.min(idSeq, sentSeq) : (idSeq ?? sentSeq);
      if (isWorkResumeStub(stripWorkInjectedPrompt(message.content))) {
        hiddenMessageIds.add(message.id);
        const prev = runs[runs.length - 1];
        if (prev?.userId) {
          boundaries.push({ id: prev.userId, seq, ts });
          if (Number.isFinite(Number(seq))) resumedAtSeqById.set(prev.userId, Number(seq));
        }
        continue;
      }
      runs.push({ startTs: ts, userId: message.id, replies: [] });
      boundaries.push({ id: message.id, seq, ts });
      continue;
    }
    if (message.role !== 'assistant') continue;
    // A branch link is a display record, not a reply from this task's runner.
    // Leave it visible, but keep the preceding run's receipt on its own reply.
    if (message.parameters?.workTaskHandoff?.conversationId) continue;
    if (isWorkEmptyLiveDraft(message)) {
      hiddenMessageIds.add(message.id);
      continue;
    }
    if (isWorkInternalBookkeeping(message.content) && !carriesDeliverable(message)) {
      hiddenMessageIds.add(message.id);
      continue;
    }
    if (runs.length === 0) runs.push({ startTs: null, userId: null, replies: [] });
    runs[runs.length - 1].replies.push(message);
    const turnIndex = turnIndexByMessageId[message.id];
    boundaries.push({
      id: message.id,
      seq: workSeqFromMessageId(message.id)
        ?? (Number.isInteger(turnIndex) && Number.isFinite(Number(turns[turnIndex]?.seq))
          ? Number(turns[turnIndex].seq)
          : null),
      ts,
    });
  }

  // ── Chronological attachment ────────────────────────────────────────────
  // Both streams are already ordered (lines by event seq, rows by transcript
  // position), so a single forward walk places every line under the last row
  // it followed. Seq wins when both sides have one; timestamps cover rows the
  // sync has not renamed yet. Turn lines (boot/thinking) are live status, not
  // history — they never enter a trail, so they never vanish from one.
  const allLines = [];
  for (const turn of turns) {
    for (const line of turn.lines) {
      if (line?.kind !== 'turn') allLines.push(line);
      else if (line.icon === 'compress') allLines.push({ ...line, kind: 'info' });
    }
  }
  if (live?.lines) {
    for (const line of live.lines) {
      if (line?.kind !== 'turn') allLines.push(line);
      else if (line.icon === 'compress') allLines.push({ ...line, kind: 'info' });
    }
  }

  // <= on seq: the "your message is queued" note shares its seq with the
  // steer bubble anchored to it and must render underneath, not above.
  const lineFollowsRow = (line, boundary) => {
    const lineSeq = Number(line?.key);
    if (boundary.seq !== null && Number.isFinite(lineSeq)) return boundary.seq <= lineSeq;
    const lineTs = MS(line?.ts);
    if (boundary.ts !== null && lineTs !== null) return boundary.ts <= lineTs;
    return true;
  };

  const trailingById = new Map();
  let bi = -1;
  for (const line of allLines) {
    while (bi + 1 < boundaries.length && lineFollowsRow(line, boundaries[bi + 1])) bi += 1;
    // Boot noise from before the first request renders under it rather than
    // being dropped — the trail is a log, not a summary.
    const target = bi >= 0 ? boundaries[bi] : boundaries[0];
    if (!target) continue;
    const resumedAtSeq = resumedAtSeqById.get(target.id);
    const lineSeq = Number(line?.key);
    if ((line.kind === 'divider' || line.kind === 'end')
      && Number.isFinite(resumedAtSeq)
      && Number.isFinite(lineSeq)
      && lineSeq < resumedAtSeq) {
      continue;
    }
    const bucket = trailingById.get(target.id);
    if (bucket) bucket.push(line);
    else trailingById.set(target.id, [line]);
  }

  // Receipts are durable server totals for individual execution turns. Match
  // by turn ID, then timeline for old logs. Resuming a paused request combines
  // its segments; a new follow-up never inherits a prior request's charges.
  const runIndexByRowId = new Map();
  runs.forEach((run, index) => {
    for (const id of [run.userId, ...run.replies.map(message => message.id)]) {
      if (id) runIndexByRowId.set(id, index);
    }
  });
  const runIndexByTurnId = new Map();
  const outcomeByRun = new Map();
  const terminalByRun = new Map();
  let receiptBoundary = -1;
  for (const event of Array.isArray(events) ? events : []) {
    while (receiptBoundary + 1 < boundaries.length
      && lineFollowsRow({ key: event.seq, ts: event.ts }, boundaries[receiptBoundary + 1])) receiptBoundary += 1;
    const target = boundaries[Math.max(0, receiptBoundary)];
    if (event.turnId && target) runIndexByTurnId.set(event.turnId, runIndexByRowId.get(target.id));
    const index = target ? runIndexByRowId.get(target.id) : undefined;
    if (index === undefined) continue;
    // "completed" also closes approval turns. Retain the actual end reason
    // and its matching response so replay never presents that pause as success.
    const outcome = outcomeByRun.get(index) || { endReason: null, responseReceived: false };
    if (event.type === 'session_end') outcome.endReason = event.reason || null;
    if (event.type === 'user_question_requested') {
      outcome.questionId = event.id;
      outcome.responseReceived = false;
    } else if (event.type === 'user_question_resolved' && event.id && event.id === outcome.questionId
      && !event.dismissed && !event.timedOut
      && (event.skipped === true || typeof event.answer === 'string' || event.optionId)) {
      outcome.responseReceived = true;
    }
    outcomeByRun.set(index, outcome);
    if (event.type === 'session_end' && target) terminalByRun.set(runIndexByRowId.get(target.id), event);
  }
  const creditsByRun = new Map();
  for (const receipt of creditReceipts || []) {
    if (receipt.creditsUsed == null || !Number.isFinite(Number(receipt.creditsUsed))) continue;
    let index = runIndexByTurnId.get(receipt.turnId);
    if (index === undefined) {
      const finishedAt = MS(receipt.finishedAt);
      if (finishedAt === null) continue;
      // Last request already present when this receipt settled. This also
      // keeps receipts attached while the event log is loading on reopen.
      for (let i = 0; i < runs.length; i += 1) {
        if (runs[i].startTs !== null && runs[i].startTs <= finishedAt) index = i;
      }
    }
    if (index !== undefined) creditsByRun.set(index,
      (creditsByRun.get(index) || 0) + Math.max(0, Number(receipt.creditsUsed)));
  }

  const byMessageId = {};
  for (const [id, lines] of trailingById) {
    byMessageId[id] = { activityLines: lines };
  }
  // Only the newest trail is still being written to; it carries the live
  // styling (current-step highlight, new-line ease-in).
  if (isActive && boundaries.length) {
    const last = boundaries[boundaries.length - 1];
    byMessageId[last.id] = { ...(byMessageId[last.id] || {}), isLiveTrail: true };
  }

  // Keep one browser artifact per request, anchored where browsing began.
  // Project it from persisted events so reopening the chat restores the card.
  const browserByRow = new Map();
  let browserBoundary = -1;
  for (const ev of Array.isArray(events) ? events : []) {
    if (ev?.type !== 'tool_execution_start'
      || !String(ev.toolName || ev.name || '').startsWith('browser_')) continue;
    const line = { key: ev.seq, ts: ev.ts };
    while (browserBoundary + 1 < boundaries.length && lineFollowsRow(line, boundaries[browserBoundary + 1])) browserBoundary += 1;
    const target = boundaries[Math.max(0, browserBoundary)];
    if (!target) continue;
    const previous = browserByRow.get(target.id);
    browserByRow.set(target.id, { url: toolArgs(ev).url || previous?.url || '' });
  }

  let liveRunHasReply = false;
  let liveRunStartTs = null;
  runs.forEach((run, runIndex) => {
    const isLive = isActive && runIndex === runs.length - 1;
    const isLatestRun = runIndex === runs.length - 1;
    if (isLive && run.replies.length > 0) liveRunHasReply = true;
    const browserRows = [run.userId, ...run.replies.map((m) => m.id)].filter((id) => browserByRow.has(id));
    if (browserRows.length) {
      const anchor = browserRows[0];
      const url = browserRows.map((id) => browserByRow.get(id).url).filter(Boolean).pop() || '';
      byMessageId[anchor] = {
        ...(byMessageId[anchor] || {}),
        browserArtifact: { id: run.userId || anchor, url, isLive },
      };
    }

    let startTs = run.startTs;
    run.replies.forEach((message, replyIndex) => {
      const isFinal = replyIndex === run.replies.length - 1;
      const turnIndex = turnIndexByMessageId[message.id];
      const turn = Number.isInteger(turnIndex) ? turns[turnIndex] : null;
      if (startTs === null && turn?.startTs != null) startTs = turn.startTs;

      const mine = byMessageId[message.id]?.activityLines ?? [];
      byMessageId[message.id] = {
        ...(byMessageId[message.id] || {}),
        stepSummary: summarizeWorkSteps(mine, { isLive: isLive && isFinal }),
        isLiveStep: isLive && isFinal,
        isFinalReply: isFinal,
        isLatestRun,
      };
    });
    if (isLive) liveRunStartTs = startTs === null ? null
      : startTs + workRunPausedMs(events, startTs, Date.now());

    // The run's account — elapsed time and this turn's credits — closes the run
    // under its final reply, after the deliverables, the way a receipt
    // follows the goods. While the run is live, the status row at the end of
    // the transcript carries the ticking clock instead.
    const finalReply = run.replies[run.replies.length - 1] || null;
    if (!finalReply || isLive) return;

    const runRowIds = [run.userId, ...run.replies.map((m) => m.id)].filter(Boolean);
    const detailLines = runRowIds.flatMap((id) => byMessageId[id]?.activityLines ?? []);
    const recordedTerminal = terminalByRun.get(runIndex);
    const replyTs = MS(finalReply.timestamp || finalReply.date || finalReply.createdAt);
    // An explicit Resume can reuse the same request after an earlier stop.
    const terminal = MS(recordedTerminal?.ts) !== null && (replyTs === null || MS(recordedTerminal.ts) >= replyTs)
      ? recordedTerminal : null;
    // A progress note can be the last prose before cancellation. The work
    // continued after that note, so its timestamp is not the stopping time.
    const interrupted = ['stopped', 'interrupted', 'failed', 'budget_exceeded'].includes(terminal?.status);
    const endTs = interrupted ? MS(terminal.ts) : replyTs;
    const elapsed = (startTs !== null && endTs !== null)
      ? endTs - startTs - workRunPausedMs(events, startTs, endTs)
      : null;
    const runCredits = creditsByRun.get(runIndex) ?? null;
    const costNote = formatWorkCostNote({
      creditsUsed: runCredits,
    });

    byMessageId[finalReply.id] = {
      ...(byMessageId[finalReply.id] || {}),
      isRunEnd: true,
      endReason: outcomeByRun.get(runIndex)?.endReason || null,
      responseReceived: outcomeByRun.get(runIndex)?.responseReceived === true,
      terminalStatus: terminal?.status || null,
      durationMs: (elapsed !== null && elapsed > 0 && elapsed < MAX_FALLBACK_TURN_MS) ? elapsed : null,
      attribution: summarizeWorkSteps(detailLines.filter(line => line.kind === 'tool' && !NAMED_ACTOR_TOOLS.has(line.name))),
      creditsUsed: runCredits,
      creditScope: 'run',
      costNote,
    };
  });

  // A worker can finish a tool-only turn without assistant prose (a quick
  // edit to an existing artifact, or a first turn that never wrote back).
  // Close every silent run that has a terminal event — not only the latest —
  // so an empty first turn keeps its receipt after a later follow-up lands.
  const latestRun = runs[runs.length - 1] || null;
  {
    const terminalStatuses = new Map((Array.isArray(events) ? events : [])
      .filter(event => event?.type === 'session_end')
      .map(event => [event.seq, event]));
    for (const [runIndex, run] of runs.entries()) {
      if (isActive && run === latestRun) continue;
      if (!run?.userId || run.replies.length > 0) continue;
      const detailLines = byMessageId[run.userId]?.activityLines ?? [];
      const terminalLine = [...detailLines].reverse().find((line) => (
        line?.kind === 'divider'
        || line?.kind === 'end'
        || /^Ended \(/.test(String(line?.text || ''))
        || /^Paused after/.test(String(line?.text || ''))
      ));
      if (!terminalLine) continue;
      const isLatest = run === latestRun;
      const endTs = MS(terminalLine.ts);
      const elapsed = (run.startTs !== null && endTs !== null)
        ? endTs - run.startTs - workRunPausedMs(events, run.startTs, endTs)
        : null;
      const runCredits = creditsByRun.get(runIndex) ?? null;
      byMessageId[run.userId] = {
        ...(byMessageId[run.userId] || {}),
        isRunEnd: true,
        isLatestRun: isLatest,
        settledWithoutReply: true,
        endReason: outcomeByRun.get(runIndex)?.endReason || null,
        responseReceived: outcomeByRun.get(runIndex)?.responseReceived === true,
        // The task can finish a later request without changing this run's
        // stopped/paused outcome. Preserve the status from its own event.
        terminalStatus: terminalStatuses.get(terminalLine.key)?.status || null,
        waitingOnUser: terminalStatuses.get(terminalLine.key)?.reason === 'waiting_on_user',
        // A tool-only edit that exported a file is reviewable. A first turn
        // that never wrote a reply or a deliverable is not — calling that
        // "Ready for review" is a false success.
        emptyOutcome: !workRunHasDeliverable(events, run.startTs),
        durationMs: (elapsed !== null && elapsed > 0 && elapsed < MAX_FALLBACK_TURN_MS)
          ? elapsed
          : null,
        attribution: summarizeWorkSteps(detailLines.filter(line => line.kind === 'tool' && !NAMED_ACTOR_TOOLS.has(line.name))),
        creditsUsed: runCredits,
        creditScope: 'run',
        costNote: formatWorkCostNote({ creditsUsed: runCredits }),
      };
    }
  }

  annotateWorkProgressFolds(runs, byMessageId, {
    isActive,
    pendingUserGate: workHasPendingUserGate(events),
  });

  return {
    byMessageId,
    hiddenMessageIds,
    // While the run is live, the transcript ends with a single status row —
    // what the agent is doing right now plus the run's clock, updating in
    // place. Steps live in the trails above; this row never accumulates
    // history, so nothing moves when it goes away at settle.
    live: isActive
      ? {
        startTs: liveRunStartTs ?? live?.startTs ?? null,
        summary: summarizeWorkSteps(live?.lines, { isLive: true }),
        attribution: summarizeWorkAttribution(live?.lines, { isLive: true, organizerModel }),
        hasReply: liveRunHasReply,
      }
      : null,
  };
}

/**
 * Number of user directions still waiting for the running agent to pick up.
 * queue_update is a snapshot rather than an append-only count, so the newest
 * event is authoritative. Budget wrap-up messages are orchestrator control
 * traffic and must never be presented as something the user queued.
 */
export function workQueuedDirectionCount(events) {
  let count = 0;
  for (const ev of Array.isArray(events) ? events : []) {
    if (ev?.type !== 'queue_update') continue;
    if (Array.isArray(ev.steering)) {
      count = ev.steering.filter((entry) => (
        !String(entry?.message ?? entry ?? '').startsWith('BUDGET NOTICE:')
      )).length;
    } else {
      count = Math.max(0, Number(ev.queued) || 0);
    }
  }
  return count;
}

/**
 * Pages the agent opened or searched, in first-seen order — the "Sources"
 * list for the Work side rail.
 */
export function workSourcesFromEvents(events) {
  const seen = new Map();
  for (const ev of enrichWorkToolEvents(events)) {
    if (ev?.type !== 'tool_execution_start' || ev.isError) continue;
    const url = ev.args?.url;
    if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) continue;
    if (isWorkPlatformCdnUrl(url)) continue;
    // Workspace preview attempts are implementation details, not research sources.
    const host = workHostOf(url).toLowerCase();
    if (host === 'localhost' || host.endsWith('.localhost') || host === '[::1]' || /^127\./.test(host)) continue;
    if (seen.has(url)) continue;
    seen.set(url, { url, host: workHostOf(url), seq: ev.seq });
  }
  return [...seen.values()];
}

/**
 * Local file references from completed read/diff operations. These are task
 * sources, not a claim that every referenced file was cited in the answer.
 * Failed calls, searches, directory listings, and writes add no sources.
 */
export function workProjectSourcesFromEvents(events) {
  const pending = new Map();
  const seen = new Map();
  for (const event of Array.isArray(events) ? events : []) {
    if (event?.type === 'session_start' || event?.type === 'session_resume') pending.clear();
    if (!['project_files', 'project_read'].includes(event?.toolName) || !event.toolCallId) continue;
    if (event.type === 'tool_execution_start') {
      const args = toolArgs(event);
      if (!['read', 'read_many', 'git_diff'].includes(args.operation)) continue;
      const paths = args.operation === 'read_many' ? args.paths : [args.path];
      pending.set(event.toolCallId, { seq: event.seq, paths: Array.isArray(paths) ? paths : [] });
    } else if (event.type === 'tool_execution_end') {
      const start = pending.get(event.toolCallId);
      pending.delete(event.toolCallId);
      if (!start || event.isError || event.result?.isError) continue;
      const successfulPaths = event.projectReadPaths ?? event.details?.projectReadPaths ?? event.result?.details?.projectReadPaths;
      for (const value of start.paths) {
        if (Array.isArray(successfulPaths) && !successfulPaths.includes(value)) continue;
        const path = projectSourcePath(value);
        if (path && !seen.has(path)) seen.set(path, { path, seq: start.seq });
      }
    }
  }
  return [...seen.values()];
}

// The bit of a URL a person recognizes.
export function workHostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return String(url || '').replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
}

/** Gab's own media CDN — pulling generated files into the workspace is not a source. */
export function isWorkPlatformCdnUrl(url) {
  const host = workHostOf(url);
  if (!host) return false;
  return host === 'cdn.gab.ai' || host.endsWith('.cdn.gab.ai');
}

function isPlatformMediaFetch(ev) {
  const name = String(ev?.toolName || ev?.name || '').toLowerCase();
  if (name !== 'fetch_url' && name !== 'read_url' && name !== 'browser_navigate') return false;
  return isWorkPlatformCdnUrl(ev?.args?.url || toolArgs(ev).url);
}

export function workToneColor(tone, colors) {
  if (tone === 'destructive') return colors.color_destructive;
  if (tone === 'warning') return '#F59E0B';
  if (tone === 'success') return colors.color_success;
  if (tone === 'accent') return colors.accent_color_primary;
  return colors.text_color_secondary;
}
